import { useState, useEffect, useMemo, useCallback, useRef } from "react";

// ── CONSTANTS ─────────────────────────────────────────────────────────────────
const GRADE_MAP={"A+":4.33,"A":4.00,"A-":3.66,"B+":3.33,"B":3.00,"B-":2.66,"C+":2.33,"C":2.00,"C-":1.66,"D+":1.33,"D":1.00,"D-":0.66};
const BUCKETS=["A","B","C","D"];
const MODIFIERS=["+","","-"];
const BUCKET_COLOR={A:{bg:"#2B5C8A",text:"#F7F5F0",light:"rgba(43,92,138,0.12)"},B:{bg:"#1a1a1a",text:"#F7F5F0",light:"rgba(26,26,26,0.10)"},C:{bg:"#6B6B6B",text:"#F7F5F0",light:"rgba(107,107,107,0.12)"},D:{bg:"#8B0000",text:"#F7F5F0",light:"rgba(139,0,0,0.10)"}};
const BUCKET_LABEL={A:"Best Meals",B:"Good Meals",C:"Borderline"};
const SORT_OPTIONS=[{val:"name_asc",label:"Name A–Z"},{val:"name_desc",label:"Name Z–A"},{val:"grade_desc",label:"Highest Grade"},{val:"grade_asc",label:"Lowest Grade"},{val:"freq_desc",label:"Most Used"},{val:"recent",label:"Recently Logged"}];
// Activity grading
const ACT_EXERCISE_GRADE=mins=>{if(mins>=60)return"A";if(mins>=30)return"B";if(mins>=1)return"C";return"D";};
const ACT_STEPS_GRADE=steps=>{if(steps>=10000)return"A";if(steps>=7500)return"B";if(steps>=5000)return"C";return"D";};
const ACT_GRADE_COLOR={A:"#2B5C8A",B:"#1a1a1a",C:"#6B6B6B",D:"#8B0000"};
const ACT_EXERCISE_RANGES=[{grade:"A",label:"60+ min",min:60},{grade:"B",label:"30–59 min",min:30},{grade:"C",label:"1–29 min",min:1},{grade:"D",label:"0 min",min:0}];
const ACT_STEPS_RANGES=[{grade:"A",label:"10,000+",min:10000},{grade:"B",label:"7,500–9,999",min:7500},{grade:"C",label:"5,000–7,499",min:5000},{grade:"D",label:"< 5,000",min:0}];

const DOW=["Su","Mo","Tu","We","Th","Fr","Sa"];
const MONTHS=["January","February","March","April","May","June","July","August","September","October","November","December"];

// ── HELPERS ───────────────────────────────────────────────────────────────────
const gradeVal=(b,mod)=>GRADE_MAP[b+mod]??GRADE_MAP[b];
const gpaFmt=v=>(v==null||isNaN(v))?"—":v.toFixed(2);
const letterGrade=v=>{if(v==null)return"—";if(v>=4.17)return"A+";if(v>=3.84)return"A";if(v>=3.50)return"A-";if(v>=3.17)return"B+";if(v>=2.84)return"B";if(v>=2.50)return"B-";if(v>=2.17)return"C+";if(v>=1.84)return"C";if(v>=1.50)return"C-";if(v>=1.17)return"D+";if(v>=0.84)return"D";return"D-";};
const avg=arr=>arr.length?arr.reduce((s,v)=>s+v,0)/arr.length:null;
const todayStr=()=>new Date().toISOString().slice(0,10);
const fmtDate=s=>{const d=new Date(s+"T12:00:00");return`${MONTHS[d.getMonth()].slice(0,3)} ${d.getDate()}`;};
const fmtFull=s=>{const d=new Date(s+"T12:00:00");return`${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;};
const uid=()=>Math.random().toString(36).slice(2,10);
const recProtein=lbs=>Math.round((lbs||180)*0.8);
const recCalories=lbs=>Math.round((lbs||180)*14);
const GRADES_ALL=MODIFIERS.flatMap(mod=>BUCKETS.map(b=>({b,mod,val:gradeVal(b,mod)})));

function offsetDate(days){const d=new Date();d.setDate(d.getDate()+days);return d.toISOString().slice(0,10);}
function calDaysFor(year,month){
  const first=new Date(year,month,1),startDow=first.getDay();
  const daysInMonth=new Date(year,month+1,0).getDate(),daysInPrev=new Date(year,month,0).getDate();
  const cells=[];
  for(let i=startDow-1;i>=0;i--){const d=new Date(year,month-1,daysInPrev-i);cells.push({ds:d.toISOString().slice(0,10),cur:false});}
  for(let d=1;d<=daysInMonth;d++){const dt=new Date(year,month,d);cells.push({ds:dt.toISOString().slice(0,10),cur:true});}
  const rem=42-cells.length;
  for(let d=1;d<=rem;d++){const dt=new Date(year,month+1,d);cells.push({ds:dt.toISOString().slice(0,10),cur:false});}
  return cells;
}

// ── SEED DATA ─────────────────────────────────────────────────────────────────
const NOW=Date.now();
const MEAL_SEED=[
  {id:uid(),bucket:"A",mod:"",name:"Grilled salmon + quinoa",date:offsetDate(-1),ts:NOW-86400000},
  {id:uid(),bucket:"D",mod:"",name:"Cheeseburger + fries",date:offsetDate(-1),ts:NOW-72000000},
  {id:uid(),bucket:"B",mod:"",name:"Turkey sandwich",date:todayStr(),ts:NOW-7200000},
  {id:uid(),bucket:"C",mod:"",name:"Pasta alfredo",date:todayStr(),ts:NOW-3600000},
  // Library history – A meals
  {id:uid(),bucket:"A",mod:"+",name:"Greek yogurt + berries",date:offsetDate(-30),ts:NOW-2592000000},
  {id:uid(),bucket:"A",mod:"-",name:"Egg white omelette",date:offsetDate(-31),ts:NOW-2678400000},
  {id:uid(),bucket:"A",mod:"",name:"Avocado toast + poached eggs",date:offsetDate(-32),ts:NOW-2764800000},
  {id:uid(),bucket:"A",mod:"+",name:"Overnight oats + chia + blueberries",date:offsetDate(-33),ts:NOW-2851200000},
  {id:uid(),bucket:"A",mod:"",name:"Grilled chicken breast + broccoli",date:offsetDate(-34),ts:NOW-2937600000},
  {id:uid(),bucket:"A",mod:"-",name:"Tuna salad lettuce wraps",date:offsetDate(-35),ts:NOW-3024000000},
  {id:uid(),bucket:"A",mod:"",name:"Baked cod + roasted asparagus",date:offsetDate(-36),ts:NOW-3110400000},
  {id:uid(),bucket:"A",mod:"+",name:"Açaí bowl + granola + banana",date:offsetDate(-37),ts:NOW-3196800000},
  {id:uid(),bucket:"A",mod:"",name:"Steak + sweet potato + spinach",date:offsetDate(-38),ts:NOW-3283200000},
  {id:uid(),bucket:"A",mod:"-",name:"Turkey + avocado collard wrap",date:offsetDate(-39),ts:NOW-3369600000},
  {id:uid(),bucket:"A",mod:"",name:"Shrimp stir-fry + brown rice",date:offsetDate(-40),ts:NOW-3456000000},
  {id:uid(),bucket:"A",mod:"+",name:"Cottage cheese + pineapple",date:offsetDate(-41),ts:NOW-3542400000},
  {id:uid(),bucket:"A",mod:"",name:"Lemon herb grilled chicken + kale",date:offsetDate(-42),ts:NOW-3628800000},
  {id:uid(),bucket:"A",mod:"-",name:"Veggie egg scramble + salsa",date:offsetDate(-43),ts:NOW-3715200000},
  {id:uid(),bucket:"A",mod:"",name:"Smoked salmon + cucumber bites",date:offsetDate(-44),ts:NOW-3801600000},
  {id:uid(),bucket:"A",mod:"+",name:"Protein smoothie + spinach + banana",date:offsetDate(-45),ts:NOW-3888000000},
  {id:uid(),bucket:"A",mod:"",name:"Bison burger + lettuce bun",date:offsetDate(-46),ts:NOW-3974400000},
  {id:uid(),bucket:"A",mod:"-",name:"Edamame + brown rice bowl",date:offsetDate(-47),ts:NOW-4060800000},
  {id:uid(),bucket:"A",mod:"",name:"Grilled tilapia + steamed veggies",date:offsetDate(-48),ts:NOW-4147200000},
  {id:uid(),bucket:"A",mod:"+",name:"Hard-boiled eggs + apple slices",date:offsetDate(-49),ts:NOW-4233600000},
  {id:uid(),bucket:"A",mod:"",name:"Chicken + roasted brussels sprouts",date:offsetDate(-50),ts:NOW-4320000000},
  {id:uid(),bucket:"A",mod:"-",name:"Zucchini noodles + turkey marinara",date:offsetDate(-51),ts:NOW-4406400000},
  {id:uid(),bucket:"A",mod:"",name:"Sardines + whole grain crackers",date:offsetDate(-52),ts:NOW-4492800000},
  {id:uid(),bucket:"A",mod:"+",name:"Kale Caesar + grilled chicken",date:offsetDate(-53),ts:NOW-4579200000},
  // Library history – B meals
  {id:uid(),bucket:"B",mod:"+",name:"Brown rice + chicken thighs",date:offsetDate(-30),ts:NOW-2595600000},
  {id:uid(),bucket:"B",mod:"-",name:"Chicken caesar salad",date:offsetDate(-31),ts:NOW-2682000000},
  {id:uid(),bucket:"B",mod:"",name:"Lentil soup + sourdough",date:offsetDate(-32),ts:NOW-2768400000},
  {id:uid(),bucket:"B",mod:"+",name:"Veggie burrito bowl",date:offsetDate(-33),ts:NOW-2854800000},
  {id:uid(),bucket:"B",mod:"",name:"Greek salad + pita + hummus",date:offsetDate(-34),ts:NOW-2941200000},
  {id:uid(),bucket:"B",mod:"-",name:"Oatmeal + honey + walnuts",date:offsetDate(-35),ts:NOW-3027600000},
  {id:uid(),bucket:"B",mod:"",name:"Black bean tacos + corn tortilla",date:offsetDate(-36),ts:NOW-3114000000},
  {id:uid(),bucket:"B",mod:"+",name:"Whole wheat pasta + tomato sauce",date:offsetDate(-37),ts:NOW-3200400000},
  {id:uid(),bucket:"B",mod:"",name:"Chicken + sweet potato hash",date:offsetDate(-38),ts:NOW-3286800000},
  {id:uid(),bucket:"B",mod:"-",name:"Egg salad on multigrain",date:offsetDate(-39),ts:NOW-3373200000},
  {id:uid(),bucket:"B",mod:"",name:"Sushi roll + miso soup",date:offsetDate(-40),ts:NOW-3459600000},
  {id:uid(),bucket:"B",mod:"+",name:"Steak + roasted potatoes",date:offsetDate(-41),ts:NOW-3546000000},
  {id:uid(),bucket:"B",mod:"",name:"Grilled cheese on whole grain",date:offsetDate(-42),ts:NOW-3632400000},
  {id:uid(),bucket:"B",mod:"-",name:"Tomato bisque + half sandwich",date:offsetDate(-43),ts:NOW-3718800000},
  {id:uid(),bucket:"B",mod:"",name:"Turkey sandwich on whole wheat",date:offsetDate(-44),ts:NOW-3805200000},
  // Library history – C meals
  {id:uid(),bucket:"C",mod:"-",name:"Veggie pizza",date:offsetDate(-30),ts:NOW-2599200000},
  {id:uid(),bucket:"C",mod:"+",name:"Cheese quesadilla",date:offsetDate(-31),ts:NOW-2685600000},
  {id:uid(),bucket:"C",mod:"",name:"Fried rice takeout",date:offsetDate(-32),ts:NOW-2772000000},
  {id:uid(),bucket:"C",mod:"-",name:"Bagel + cream cheese",date:offsetDate(-33),ts:NOW-2858400000},
  {id:uid(),bucket:"C",mod:"",name:"Mac and cheese (boxed)",date:offsetDate(-34),ts:NOW-2944800000},
  {id:uid(),bucket:"C",mod:"+",name:"Pancakes + maple syrup",date:offsetDate(-35),ts:NOW-3031200000},
  {id:uid(),bucket:"C",mod:"",name:"Bean and cheese burrito",date:offsetDate(-36),ts:NOW-3117600000},
  {id:uid(),bucket:"C",mod:"-",name:"Frozen veggie lasagna",date:offsetDate(-37),ts:NOW-3204000000},
  {id:uid(),bucket:"C",mod:"",name:"PB&J on white bread",date:offsetDate(-38),ts:NOW-3290400000},
  {id:uid(),bucket:"C",mod:"+",name:"Overnight oats + fruit",date:offsetDate(-39),ts:NOW-3376800000},
];

// ── CSS ───────────────────────────────────────────────────────────────────────
const CSS=`
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=EB+Garamond:ital,wght@0,400;0,500;1,400&family=Bebas+Neue&family=DM+Mono:wght@400;500&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;-webkit-tap-highlight-color:transparent}
html{-webkit-text-size-adjust:100%}
:root{
  --ink:#0A0A0A;--steel:#2B5C8A;--steel-lt:#4A80B5;--steel-faint:rgba(43,92,138,0.08);
  --muted:#5A6070;--faint:#8890A0;--surface:#F7F5F0;--parchment:#EFECE5;
  --red:#8B0000;--border:rgba(26,26,26,0.13);--border-strong:rgba(26,26,26,0.26);
  --safe-bottom:env(safe-area-inset-bottom,0px);--safe-top:env(safe-area-inset-top,0px);--nav-h:56px;
}
body{background:var(--parchment);font-family:'EB Garamond',serif;color:var(--ink);min-height:100vh;overscroll-behavior:none;-webkit-font-smoothing:antialiased}
.app{max-width:430px;margin:0 auto;min-height:100vh;display:flex;flex-direction:column;background:var(--parchment);padding-bottom:calc(var(--nav-h) + var(--safe-bottom))}
.page-scroll{flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;overscroll-behavior-y:contain}
.nav{display:none}
.bottom-nav{position:fixed;bottom:0;left:50%;transform:translateX(-50%);width:100%;max-width:430px;display:flex;background:var(--ink);border-top:2px solid rgba(43,92,138,0.5);padding-bottom:var(--safe-bottom);z-index:200}
.nav-btn{flex:1;padding:10px 4px 8px;background:none;border:none;cursor:pointer;font-family:'DM Mono',monospace;font-size:8.5px;letter-spacing:.1em;color:rgba(239,236,229,0.4);text-transform:uppercase;min-height:var(--nav-h);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:3px;border-top:3px solid transparent;user-select:none}
.nav-btn.active{color:var(--steel-lt);border-top-color:var(--steel-lt)}
.nav-btn:active{opacity:.7}
.nav-icon{font-size:16px;line-height:1}
.ph{padding:18px 16px 12px;border-bottom:1px solid var(--border)}
.ph-title{font-family:'Cormorant Garamond',serif;font-size:24px;font-weight:700;letter-spacing:.02em}
.ph-sub{font-size:13px;color:var(--faint);margin-top:2px;font-style:italic}
/* HERO */
.hero{margin:16px 16px 14px;background:var(--ink);border-top:3px solid var(--steel);padding:18px 20px;color:var(--surface);position:relative;overflow:hidden}
.hero::before{content:'';position:absolute;inset:0;background:linear-gradient(135deg,rgba(43,92,138,0.18),transparent 60%);pointer-events:none}
.hero::after{content:'FITNESSGRADE';position:absolute;bottom:12px;right:16px;font-family:'Bebas Neue',sans-serif;font-size:10px;letter-spacing:.2em;color:rgba(247,245,240,0.07);pointer-events:none}
.hero-lbl{font-family:'DM Mono',monospace;font-size:9px;letter-spacing:.15em;text-transform:uppercase;color:var(--steel-lt);margin-bottom:4px}
.hero-val{font-family:'Bebas Neue',sans-serif;font-size:60px;line-height:1;letter-spacing:.02em}
.hero-ltr{font-family:'DM Mono',monospace;font-size:13px;color:var(--steel-lt);margin-top:2px;letter-spacing:.1em}
.hero-row{display:flex;gap:12px;margin-top:14px;padding-top:14px;border-top:1px solid rgba(247,245,240,0.12)}
.hero-sub{flex:1}
.hero-sub-lbl{font-family:'DM Mono',monospace;font-size:8.5px;letter-spacing:.1em;text-transform:uppercase;color:rgba(247,245,240,0.4);margin-bottom:2px}
.hero-sub-val{font-family:'Bebas Neue',sans-serif;font-size:21px;color:var(--surface);letter-spacing:.04em}
/* SECTION */
.sec{padding:0 16px 16px}
.sec-lbl{font-family:'DM Mono',monospace;font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:var(--faint);margin-bottom:10px;padding-top:16px;border-top:1px solid var(--border)}
.sec-hdr{display:flex;align-items:center;justify-content:space-between;padding:16px 16px 10px;border-top:1px solid var(--border)}
.sec-hdr-lbl{font-family:'DM Mono',monospace;font-size:9px;letter-spacing:.14em;text-transform:uppercase;color:var(--faint)}
.sec-hdr-action{font-family:'DM Mono',monospace;font-size:9px;letter-spacing:.08em;color:var(--steel-lt);background:none;border:none;cursor:pointer;padding:4px 0}
/* QUICK LOG */
.ql-col-headers{display:grid;grid-template-columns:repeat(4,1fr);gap:5px;margin-bottom:4px}
.ql-col-hdr{text-align:center;font-family:'Cormorant Garamond',serif;font-size:15px;font-weight:700;color:var(--ink);opacity:.35;padding:2px 0}
.ql-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:5px}
.ql-btn{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:14px 4px;border-radius:3px;border:1.5px solid transparent;cursor:pointer;transition:opacity .12s,transform .1s;font-family:'Cormorant Garamond',serif;min-height:56px;user-select:none}
.ql-btn:active{transform:scale(.91);opacity:.85}
.ql-grade{font-size:19px;font-weight:700;line-height:1}
.ql-pts{font-family:'DM Mono',monospace;font-size:8px;margin-top:3px;opacity:.65}
/* MEAL ITEM */
.mi{display:flex;align-items:center;gap:10px;padding:13px 16px;border-bottom:1px solid var(--border);transition:background .12s}
.mi:active{background:rgba(43,92,138,0.06)}
.mi-badge{width:44px;height:44px;border-radius:3px;display:flex;align-items:center;justify-content:center;font-family:'Cormorant Garamond',serif;font-size:16px;font-weight:700;flex-shrink:0;cursor:pointer}
.mi-info{flex:1;min-width:0;cursor:pointer}
.mi-name{font-size:15px;font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.mi-meta{font-family:'DM Mono',monospace;font-size:10px;color:var(--faint);margin-top:2px}
.cheat-tag{font-family:'DM Mono',monospace;font-size:9px;background:var(--red);color:var(--surface);border-radius:2px;padding:2px 5px;letter-spacing:.06em}
.mi-acts{display:flex;align-items:center}
.icon-btn{background:none;border:none;cursor:pointer;color:var(--faint);font-size:18px;padding:8px 6px;opacity:.5;line-height:1;min-width:36px;min-height:44px;display:flex;align-items:center;justify-content:center}
.icon-btn:active{opacity:1;color:var(--steel)}
/* OVERLAY + MODAL */
.overlay{position:fixed;inset:0;background:rgba(10,10,10,0.6);z-index:300;display:flex;align-items:flex-end;justify-content:center}
.modal{background:var(--surface);border-top:3px solid var(--steel);width:100%;max-width:430px;max-height:92vh;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:0 0 calc(20px + var(--safe-bottom))}
.modal-drag{width:38px;height:3px;background:var(--border-strong);border-radius:999px;margin:12px auto 14px}
.modal-title{font-family:'Cormorant Garamond',serif;font-size:21px;font-weight:700;padding:0 16px 12px;border-bottom:2px solid var(--border-strong);color:var(--ink)}
.modal-sub{font-family:'DM Mono',monospace;font-size:10px;letter-spacing:.06em;color:var(--faint);display:block;margin-top:3px;font-style:normal}
/* FORM */
.fm-row{padding:12px 16px;border-bottom:1px solid var(--border)}
.fm-lbl{font-family:'DM Mono',monospace;font-size:9px;letter-spacing:.12em;text-transform:uppercase;color:var(--faint);margin-bottom:8px}
.fm-lbl-row{display:flex;align-items:center;justify-content:space-between;margin-bottom:8px}
.fm-input{width:100%;border:1.5px solid var(--border-strong);border-radius:2px;background:var(--parchment);padding:12px 14px;font-family:'EB Garamond',serif;font-size:16px;color:var(--ink);outline:none;-webkit-appearance:none}
.fm-input:focus{border-color:var(--steel)}
.fm-input::placeholder{color:var(--faint);font-style:italic}
.fm-actions{display:flex;gap:8px;padding:12px 16px 0}
.btn-primary{flex:1;padding:13px;border:none;cursor:pointer;background:var(--steel);color:var(--surface);font-family:'DM Mono',monospace;font-size:11px;letter-spacing:.1em;text-transform:uppercase;min-height:48px}
.btn-primary:active{opacity:.8}
.btn-ghost{padding:13px 16px;border:1.5px solid var(--border-strong);background:none;cursor:pointer;font-family:'DM Mono',monospace;font-size:11px;color:var(--muted);letter-spacing:.08em;min-height:48px}
.btn-ghost:active{background:rgba(0,0,0,0.04)}
.btn-danger{padding:13px 16px;border:1.5px solid var(--red);background:none;cursor:pointer;font-family:'DM Mono',monospace;font-size:11px;color:var(--red);letter-spacing:.08em;min-height:48px}
.btn-danger:active{background:rgba(139,0,0,0.06)}
.fm-rec{font-family:'DM Mono',monospace;font-size:9px;color:var(--steel-lt);cursor:pointer;background:none;border:none;padding:0}
/* GRADE PICKER */
.gp-col-headers{display:grid;grid-template-columns:repeat(4,1fr);gap:5px;margin-bottom:4px}
.gp-col-hdr{text-align:center;font-family:'Cormorant Garamond',serif;font-size:14px;font-weight:700;color:var(--ink);opacity:.3}
.gp-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:5px}
.gp-btn{display:flex;flex-direction:column;align-items:center;padding:10px 4px;border-radius:3px;border:1.5px solid transparent;cursor:pointer;font-family:'Cormorant Garamond',serif;min-height:48px;user-select:none}
.gp-btn:active{opacity:.85;transform:scale(.93)}
.gp-grade{font-size:16px;font-weight:700;line-height:1}
.gp-pts{font-family:'DM Mono',monospace;font-size:8px;margin-top:2px;opacity:.65}
/* CALENDAR */
.cal-nav{display:flex;align-items:center;justify-content:space-between;padding:0 16px 14px}
.cal-month{font-family:'Cormorant Garamond',serif;font-size:21px;font-weight:700}
.cal-arrow{background:none;border:none;cursor:pointer;font-size:22px;color:var(--ink);padding:4px 12px;min-width:44px;min-height:44px;display:flex;align-items:center;justify-content:center}
.cal-arrow:active{background:rgba(43,92,138,0.12)}
.cal-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:2px;padding:0 16px}
.cal-dow{text-align:center;font-family:'DM Mono',monospace;font-size:9px;color:var(--faint);padding-bottom:5px}
.cal-day{aspect-ratio:1;border-radius:2px;cursor:pointer;transition:background .12s;display:flex;flex-direction:column;align-items:center;justify-content:flex-start;padding-top:4px;overflow:hidden}
.cal-day:active{background:rgba(43,92,138,0.12)!important}
.cal-day.today{background:var(--steel-faint);outline:2px solid var(--steel)}
.cal-day.selected{background:var(--steel)!important;outline:none}
.cal-day.other-month{opacity:.28}
.cal-day.has-entry{background:rgba(27,94,59,0.06)}
.cal-dn{font-family:'DM Mono',monospace;font-size:11px;color:var(--ink);line-height:1}
.cal-day.selected .cal-dn{color:var(--surface)}
.cal-dots{display:flex;gap:2px;flex-wrap:wrap;justify-content:center;padding:2px 2px 0}
.cal-dot{width:4px;height:4px;border-radius:50%;flex-shrink:0}
.cal-day-gpa{font-family:'DM Mono',monospace;font-size:7.5px;color:var(--steel);margin-top:1px;line-height:1}
.cal-day.selected .cal-day-gpa{color:rgba(247,245,240,.8)}
.cal-day-wt{font-family:'DM Mono',monospace;font-size:7px;color:var(--steel);margin-top:1px;line-height:1}
.cal-day.selected .cal-day-wt{color:rgba(247,245,240,.8)}
.cal-day-nut{width:4px;height:4px;border-radius:50%;background:#2E8B57;margin-top:2px}
.cal-day.selected .cal-day-nut{background:rgba(247,245,240,.7)}
/* DAY PANEL */
.day-panel{margin:14px 16px 0;background:var(--surface);border:1.5px solid var(--border-strong);border-top:3px solid var(--steel);overflow:hidden}
.dp-header{padding:12px 14px;display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid var(--border)}
.dp-date{font-family:'Cormorant Garamond',serif;font-size:16px;font-weight:700}
.dp-gpa{font-family:'DM Mono',monospace;font-size:11px;color:var(--steel)}
.dp-add{background:var(--steel);color:var(--surface);border:none;cursor:pointer;font-family:'DM Mono',monospace;font-size:10px;letter-spacing:.08em;padding:9px 14px;min-height:38px}
.dp-add:active{opacity:.8}
.dp-empty{padding:20px;text-align:center;color:var(--faint);font-style:italic;font-size:14px}
/* STATS */
.stat-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;padding:0 16px 18px}
.sc{background:var(--surface);border:1.5px solid var(--border-strong);padding:13px 15px;border-top:3px solid var(--border-strong)}
.sc.full{grid-column:span 2}
.sc-lbl{font-family:'DM Mono',monospace;font-size:9px;letter-spacing:.12em;text-transform:uppercase;color:var(--faint);margin-bottom:4px}
.sc-val{font-family:'Bebas Neue',sans-serif;font-size:30px;letter-spacing:.04em}
.sc-ltr{font-family:'DM Mono',monospace;font-size:11px;color:var(--steel);letter-spacing:.06em}
.dist-bar{display:flex;height:8px;overflow:hidden;gap:2px}
.dist-seg{transition:flex .4s}
.dist-legend{display:flex;gap:10px;margin-top:8px;flex-wrap:wrap}
.dist-dot{width:7px;height:7px;border-radius:50%;display:inline-block;margin-right:4px}
.dist-item{display:flex;align-items:center;font-family:'DM Mono',monospace;font-size:10px;color:var(--faint)}
/* LIBRARY */
.lib-toolbar{padding:0 16px 12px;display:flex;flex-direction:column;gap:9px}
.lib-search-wrap{position:relative}
.lib-search{width:100%;border:1.5px solid var(--border-strong);border-radius:2px;background:var(--surface);padding:11px 14px 11px 36px;font-family:'EB Garamond',serif;font-size:16px;color:var(--ink);outline:none;-webkit-appearance:none}
.lib-search:focus{border-color:var(--steel)}
.lib-search::placeholder{color:var(--faint);font-style:italic}
.lib-search-icon{position:absolute;left:12px;top:50%;transform:translateY(-50%);color:var(--faint);font-size:14px;pointer-events:none}
.lib-search-clear{position:absolute;right:4px;top:50%;transform:translateY(-50%);background:none;border:none;cursor:pointer;color:var(--faint);font-size:18px;line-height:1;padding:8px;min-width:44px;min-height:44px;display:flex;align-items:center;justify-content:center}
.lib-filters{display:flex;gap:6px;overflow-x:auto;scrollbar-width:none;-webkit-overflow-scrolling:touch}
.lib-filters::-webkit-scrollbar{display:none}
.lib-filter-btn{flex-shrink:0;padding:8px 14px;border-radius:999px;border:1.5px solid var(--border-strong);background:none;cursor:pointer;font-family:'DM Mono',monospace;font-size:10px;letter-spacing:.05em;color:var(--muted);white-space:nowrap;min-height:38px}
.lib-filter-btn.active{background:var(--steel);border-color:var(--steel);color:var(--surface)}
.lib-filter-btn:active{opacity:.8}
.lib-sort-row{display:flex;align-items:center;gap:8px}
.lib-sort-lbl{font-family:'DM Mono',monospace;font-size:9px;letter-spacing:.08em;text-transform:uppercase;color:var(--faint);white-space:nowrap}
.lib-sort{flex:1;border:1.5px solid var(--border-strong);border-radius:2px;background:var(--surface);padding:9px 28px 9px 10px;font-family:'DM Mono',monospace;font-size:14px;color:var(--ink);outline:none;cursor:pointer;-webkit-appearance:none;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%235A6070'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 10px center;min-height:42px}
.lib-results-count{font-family:'DM Mono',monospace;font-size:9px;letter-spacing:.06em;text-transform:uppercase;color:var(--faint);padding:0 16px 8px}
.lib-add-btn{display:flex;align-items:center;gap:8px;margin:0 16px 14px;padding:13px 16px;border:1.5px dashed var(--steel);background:var(--steel-faint);cursor:pointer;color:var(--steel);font-family:'DM Mono',monospace;font-size:11px;letter-spacing:.08em;min-height:48px}
.lib-add-btn:active{background:rgba(43,92,138,0.16)}
.lib-card{margin:0 16px 8px;background:var(--surface);border:1.5px solid var(--border-strong);overflow:hidden}
.lib-card:active{border-color:var(--steel)}
.lib-card-main{display:flex;align-items:center;gap:10px;padding:12px}
.lib-card-badge{width:44px;height:44px;border-radius:2px;display:flex;align-items:center;justify-content:center;font-family:'Cormorant Garamond',serif;font-size:17px;font-weight:700;flex-shrink:0}
.lib-card-info{flex:1;min-width:0}
.lib-card-name{font-size:15px;font-weight:500;line-height:1.2}
.lib-card-meta{display:flex;align-items:center;gap:6px;margin-top:4px;flex-wrap:wrap}
.lib-card-pts{font-family:'DM Mono',monospace;font-size:10px;color:var(--faint)}
.lib-card-freq{font-family:'DM Mono',monospace;font-size:9px;background:rgba(43,92,138,0.1);color:var(--steel);border-radius:2px;padding:2px 6px;letter-spacing:.04em}
.lib-card-last{font-family:'DM Mono',monospace;font-size:9px;color:var(--faint)}
.lib-card-acts{display:flex;flex-direction:column;gap:5px;align-items:flex-end;flex-shrink:0}
.lib-relog-btn{background:var(--steel);color:var(--surface);border:none;cursor:pointer;font-family:'DM Mono',monospace;font-size:10px;letter-spacing:.07em;padding:8px 11px;white-space:nowrap;min-height:34px}
.lib-relog-btn:active{opacity:.75}
.lib-card-icon-acts{display:flex}
.lib-icon-btn{background:none;border:none;cursor:pointer;color:var(--faint);font-size:16px;padding:6px;opacity:.5;min-width:36px;min-height:36px;display:flex;align-items:center;justify-content:center}
.lib-icon-btn:active{opacity:1;color:var(--steel)}
.lib-card-expand{border-top:1px solid var(--border);padding:10px 12px;background:rgba(239,236,229,0.5)}
.lib-hist-row{display:flex;align-items:center;justify-content:space-between;padding:6px 0;border-bottom:1px solid var(--border)}
.lib-hist-row:last-child{border-bottom:none}
.lib-hist-date{font-family:'DM Mono',monospace;font-size:10px;color:var(--faint)}
.lib-hist-grade{font-family:'Cormorant Garamond',serif;font-size:14px;font-weight:700}
.lib-hist-del{background:none;border:none;cursor:pointer;color:var(--faint);font-size:16px;opacity:.4;min-width:36px;min-height:36px;display:flex;align-items:center;justify-content:center}
.lib-hist-del:active{opacity:1;color:var(--red)}
.lib-empty{text-align:center;padding:32px 16px;color:var(--faint);font-style:italic;font-size:15px}
.lib-bucket-divider{display:flex;align-items:center;gap:10px;margin:4px 16px 10px;padding-top:12px;border-top:1px solid var(--border)}
.lib-buc-badge{width:26px;height:26px;border-radius:2px;display:flex;align-items:center;justify-content:center;font-family:'Cormorant Garamond',serif;font-size:14px;font-weight:700;flex-shrink:0}
.lib-buc-label{font-family:'Cormorant Garamond',serif;font-size:15px;font-weight:700}
.lib-buc-count{font-family:'DM Mono',monospace;font-size:10px;color:var(--faint)}
.lm-preview{display:flex;align-items:center;gap:12px;padding:12px 16px;border-bottom:1px solid var(--border);background:var(--steel-faint)}
.lm-prev-badge{width:48px;height:48px;border-radius:3px;display:flex;align-items:center;justify-content:center;font-family:'Cormorant Garamond',serif;font-size:22px;font-weight:700}
.lm-prev-name{font-family:'Cormorant Garamond',serif;font-size:17px;font-weight:700}
.lm-prev-meta{font-family:'DM Mono',monospace;font-size:11px;color:var(--faint);margin-top:2px}
/* INLINE EDIT */
.inline-num{background:none;border:none;outline:none;font-family:'Bebas Neue',sans-serif;letter-spacing:.02em;color:var(--ink);text-align:center;width:100%;cursor:text;border-bottom:2px solid transparent;-webkit-appearance:none;padding:0}
.inline-num:focus{border-bottom-color:var(--steel)}
.inline-num.weight-size{font-size:80px;line-height:1}
.inline-num.nut-size{font-size:52px;line-height:1}
/* WEIGHT STEPPER */
.w-hero{margin:16px 16px 0;background:var(--ink);border-top:3px solid var(--steel);padding:18px 20px;color:var(--surface);position:relative;overflow:hidden}
.w-hero::before{content:'';position:absolute;inset:0;background:linear-gradient(135deg,rgba(43,92,138,0.22),transparent 60%);pointer-events:none}
.w-hero-label{font-family:'DM Mono',monospace;font-size:9px;letter-spacing:.15em;text-transform:uppercase;color:var(--steel-lt);margin-bottom:4px}
.w-hero-val{font-family:'Bebas Neue',sans-serif;font-size:64px;line-height:1;letter-spacing:.02em}
.w-hero-unit{font-family:'DM Mono',monospace;font-size:13px;color:rgba(247,245,240,0.55);margin-left:4px;vertical-align:middle}
.w-hero-delta{font-family:'DM Mono',monospace;font-size:12px;margin-top:4px;letter-spacing:.06em}
.w-hero-delta.up{color:#EF9090}.w-hero-delta.down{color:#7EC8A0}.w-hero-delta.flat{color:rgba(247,245,240,0.4)}
.w-hero-row{display:flex;gap:16px;margin-top:14px;padding-top:14px;border-top:1px solid rgba(247,245,240,0.1)}
.w-stat{flex:1}
.w-stat-lbl{font-family:'DM Mono',monospace;font-size:8.5px;letter-spacing:.1em;text-transform:uppercase;color:rgba(247,245,240,0.38);margin-bottom:2px}
.w-stat-val{font-family:'Bebas Neue',sans-serif;font-size:20px;color:var(--surface);letter-spacing:.04em}
.w-hero-btn{position:absolute;top:16px;right:16px;background:rgba(43,92,138,0.35);border:1px solid rgba(43,92,138,0.5);color:var(--surface);padding:7px 13px;font-family:'DM Mono',monospace;font-size:10px;letter-spacing:.08em;cursor:pointer;min-height:34px}
.w-hero-btn:active{background:rgba(43,92,138,0.55)}
.w-stepper-wrap{padding:20px 16px;border-bottom:1px solid var(--border);display:flex;flex-direction:column;align-items:center;gap:14px}
.w-stepper-unit{font-family:'DM Mono',monospace;font-size:12px;color:var(--faint);margin-top:-8px}
.w-stepper-row{display:flex;align-items:center;width:100%}
.w-step-btn{flex:1;height:60px;background:var(--parchment);border:1.5px solid var(--border-strong);font-family:'Bebas Neue',sans-serif;font-size:30px;color:var(--ink);cursor:pointer;user-select:none;display:flex;align-items:center;justify-content:center}
.w-step-btn:active{background:rgba(43,92,138,0.12)}
.w-step-btn.minus{border-right:none}
.w-step-btn.plus{border-left:none}
.w-stepper-center{width:72px;height:60px;background:var(--steel);display:flex;flex-direction:column;align-items:center;justify-content:center;font-family:'Bebas Neue',sans-serif;font-size:15px;letter-spacing:.08em;color:var(--surface);flex-shrink:0}
.w-step-hint{font-family:'DM Mono',monospace;font-size:9px;letter-spacing:.1em;color:var(--faint);text-transform:uppercase}
/* NUTRITION */
.nut-row{display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;padding:0 16px 4px}
.nut-card{background:var(--surface);border:1.5px solid var(--border-strong);padding:12px 10px;position:relative;overflow:hidden;cursor:pointer}
.nut-card:active{border-color:var(--steel)}
.nut-card-accent{position:absolute;top:0;left:0;right:0;height:3px}
.nut-card-lbl{font-family:'DM Mono',monospace;font-size:8px;letter-spacing:.1em;text-transform:uppercase;color:var(--faint);margin-bottom:5px}
.nut-card-val{font-family:'Bebas Neue',sans-serif;font-size:24px;letter-spacing:.03em;line-height:1}
.nut-card-unit{font-family:'DM Mono',monospace;font-size:8px;color:var(--faint);margin-top:3px}
.nut-card-prog{height:3px;background:var(--border);margin-top:7px;overflow:hidden}
.nut-card-prog-fill{height:100%}
.nut-card-target{font-family:'DM Mono',monospace;font-size:7.5px;color:var(--faint);margin-top:3px}
.fm-num-row{display:flex;align-items:center;height:50px}
.fm-num-btn{width:50px;height:50px;background:var(--parchment);border:1.5px solid var(--border-strong);font-size:22px;color:var(--ink);cursor:pointer;flex-shrink:0;display:flex;align-items:center;justify-content:center;user-select:none}
.fm-num-btn:active{background:rgba(43,92,138,0.1)}
.fm-num-input{flex:1;height:50px;border:1.5px solid var(--border-strong);border-left:none;border-right:none;background:var(--surface);text-align:center;font-family:'Bebas Neue',sans-serif;font-size:28px;color:var(--ink);outline:none;-webkit-appearance:none;min-width:0}
.fm-num-input:focus{border-color:var(--steel)}
/* WATER */
.water-wrap{padding:14px 16px;border-bottom:1px solid var(--border)}
.water-cells{display:flex;gap:5px;flex-wrap:nowrap;overflow-x:auto;scrollbar-width:none;-webkit-overflow-scrolling:touch;padding-bottom:2px}
.water-cells::-webkit-scrollbar{display:none}
.water-cell{flex-shrink:0;width:34px;height:34px;display:flex;align-items:center;justify-content:center;cursor:pointer;font-family:'DM Mono',monospace;font-size:11px;font-weight:500;border:1.5px solid var(--border-strong);user-select:none}
.water-cell.filled{background:var(--steel);border-color:var(--steel);color:var(--surface)}
.water-cell.empty{background:none;color:var(--border-strong)}
.water-slider{width:100%;-webkit-appearance:none;height:5px;background:var(--border);outline:none}
.water-slider::-webkit-slider-thumb{-webkit-appearance:none;width:22px;height:22px;background:var(--steel);cursor:pointer;border:none}
.water-slider::-moz-range-thumb{width:22px;height:22px;background:var(--steel);cursor:pointer;border:none}
/* CHART */
.chart-wrap{margin:4px 16px 0;background:var(--surface);border:1.5px solid var(--border-strong);padding:14px 12px}
.chart-svg{width:100%;overflow:visible}
.chart-line{fill:none;stroke:var(--steel);stroke-width:2;stroke-linejoin:round;stroke-linecap:round}
.chart-area{fill:url(#chartGrad);opacity:0.3}
.chart-dot{fill:var(--steel);cursor:pointer}
.chart-dot-today{fill:var(--surface);stroke:var(--steel);stroke-width:2.5;cursor:pointer}
.chart-axis-lbl{font-family:'DM Mono',monospace;font-size:8px;fill:var(--faint)}
.chart-range-btn{font-family:'DM Mono',monospace;font-size:9px;padding:3px 8px;background:none;border:1px solid var(--border-strong);color:var(--muted);cursor:pointer}
.chart-range-btn.active{background:var(--steel);border-color:var(--steel);color:var(--surface)}
/* HIST */
.hist-item{display:flex;align-items:center;gap:10px;padding:12px 16px;border-bottom:1px solid var(--border);cursor:pointer}
.hist-item:active{background:rgba(43,92,138,0.05)}
.hist-date{font-family:'DM Mono',monospace;font-size:10px;color:var(--faint);width:52px;flex-shrink:0}
.hist-weight{font-family:'Bebas Neue',sans-serif;font-size:22px;letter-spacing:.04em;width:56px;flex-shrink:0}
.hist-delta{font-family:'DM Mono',monospace;font-size:10px;width:38px;flex-shrink:0}
.hist-delta.up{color:var(--red)}.hist-delta.down{color:#2E8B57}
.hist-bars{flex:1;display:flex;flex-direction:column;gap:4px}
.hist-bar-row{display:flex;align-items:center;gap:5px}
.hist-bar-lbl{font-family:'DM Mono',monospace;font-size:8px;color:var(--faint);width:12px;text-align:right;flex-shrink:0}
.hist-bar-track{flex:1;height:4px;background:var(--border)}
.hist-bar-fill{height:100%}
.hist-edit-btn{background:none;border:none;cursor:pointer;color:var(--faint);font-size:14px;opacity:.5;padding:6px;min-width:32px;min-height:32px;display:flex;align-items:center;justify-content:center}
.hist-edit-btn:active{opacity:1;color:var(--steel)}
.dp-body{padding:12px 14px}
.dp-row{display:flex;align-items:center;justify-content:space-between;padding:5px 0;border-bottom:1px solid var(--border)}
.dp-row:last-child{border-bottom:none}
.dp-row-lbl{font-family:'DM Mono',monospace;font-size:10px;letter-spacing:.06em;color:var(--faint);text-transform:uppercase}
.dp-row-val{font-family:'Bebas Neue',sans-serif;font-size:18px;letter-spacing:.04em}
/* ACTIVITY */
.act-hero{margin:16px 16px 0;background:var(--ink);border-top:3px solid var(--steel);padding:18px 20px;color:var(--surface);position:relative;overflow:hidden}
.act-hero::before{content:'';position:absolute;inset:0;background:linear-gradient(135deg,rgba(43,92,138,0.22),transparent 60%);pointer-events:none}
.act-grade-row{display:flex;gap:10px;margin-top:14px}
.act-grade-pill{flex:1;display:flex;flex-direction:column;align-items:center;padding:10px 6px;border-radius:3px;cursor:pointer;transition:opacity .12s;user-select:none;border:2px solid transparent}
.act-grade-pill:active{opacity:.8}
.act-grade-pill.selected{border-color:rgba(247,245,240,0.6)}
.act-grade-letter{font-family:'Bebas Neue',sans-serif;font-size:28px;line-height:1;letter-spacing:.04em}
.act-grade-range{font-family:'DM Mono',monospace;font-size:8px;letter-spacing:.04em;margin-top:3px;opacity:.75;text-align:center;line-height:1.3}
.act-input-row{display:flex;align-items:center;gap:12px;margin-top:14px;padding-top:14px;border-top:1px solid rgba(247,245,240,0.1)}
.act-input-lbl{font-family:'DM Mono',monospace;font-size:9px;letter-spacing:.12em;text-transform:uppercase;color:rgba(247,245,240,0.5);flex-shrink:0}
.act-num-input{background:none;border:none;border-bottom:2px solid rgba(247,245,240,0.3);outline:none;font-family:'Bebas Neue',sans-serif;font-size:36px;color:var(--surface);text-align:center;width:90px;-webkit-appearance:none;padding:0 4px}
.act-num-input:focus{border-bottom-color:var(--steel-lt)}
.act-num-input::placeholder{opacity:.3}
.act-num-unit{font-family:'DM Mono',monospace;font-size:11px;color:rgba(247,245,240,0.5)}
.act-log-card{margin:8px 16px 0;background:var(--surface);border:1.5px solid var(--border-strong);border-top:3px solid var(--steel);overflow:hidden}
.act-log-hdr{padding:12px 14px;display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid var(--border)}
.act-log-date{font-family:'Cormorant Garamond',serif;font-size:16px;font-weight:700}
.act-log-gpa{font-family:'DM Mono',monospace;font-size:11px;color:var(--steel)}
.act-log-add{background:var(--steel);color:var(--surface);border:none;cursor:pointer;font-family:'DM Mono',monospace;font-size:10px;letter-spacing:.08em;padding:9px 14px;min-height:38px}
.act-log-add:active{opacity:.8}
.act-log-body{padding:12px 14px}
.act-row{display:flex;align-items:center;justify-content:space-between;padding:6px 0;border-bottom:1px solid var(--border)}
.act-row:last-child{border-bottom:none}
.act-row-lbl{font-family:'DM Mono',monospace;font-size:10px;letter-spacing:.06em;color:var(--faint);text-transform:uppercase}
.act-row-val{display:flex;align-items:center;gap:8px}
.act-row-num{font-family:'Bebas Neue',sans-serif;font-size:18px;letter-spacing:.04em}
.act-grade-badge{font-family:'DM Mono',monospace;font-size:10px;padding:2px 7px;color:var(--surface);letter-spacing:.06em}
.act-hist-item{display:flex;align-items:center;gap:10px;padding:12px 16px;border-bottom:1px solid var(--border)}
.act-hist-date{font-family:'DM Mono',monospace;font-size:10px;color:var(--faint);width:52px;flex-shrink:0}
.act-hist-grades{display:flex;gap:5px;flex:1}
.act-hist-pill{font-family:'DM Mono',monospace;font-size:9px;padding:3px 8px;color:var(--surface);letter-spacing:.05em}
.act-hist-detail{font-family:'DM Mono',monospace;font-size:9px;color:var(--faint)}
.act-stat-row{display:flex;gap:8px;padding:0 16px 8px}
.act-stat-card{flex:1;background:var(--surface);border:1.5px solid var(--border-strong);padding:12px 14px;border-top:3px solid var(--border-strong)}
.act-stat-lbl{font-family:'DM Mono',monospace;font-size:9px;letter-spacing:.1em;text-transform:uppercase;color:var(--faint);margin-bottom:4px}
.act-stat-val{font-family:'Bebas Neue',sans-serif;font-size:28px;letter-spacing:.04em}
.act-stat-sub{font-family:'DM Mono',monospace;font-size:10px;color:var(--steel)}
.act-cal-dot-ex{width:4px;height:4px;border-radius:50%;background:var(--steel);margin-top:2px}
.act-cal-dot-st{width:4px;height:4px;border-radius:50%;background:#2E8B57;margin-top:1px}
.act-modal-section{padding:14px 16px;border-bottom:1px solid var(--border)}
.act-modal-sec-lbl{font-family:'DM Mono',monospace;font-size:9px;letter-spacing:.12em;text-transform:uppercase;color:var(--faint);margin-bottom:10px}
/* TOAST */
.toast{position:fixed;top:calc(var(--safe-top,0px) + 12px);left:50%;transform:translateX(-50%) translateY(-80px);background:rgba(10,10,10,0.5);color:var(--surface);font-family:'DM Mono',monospace;font-size:11px;letter-spacing:.08em;padding:10px 20px;border-radius:2px;border-bottom:2px solid var(--steel);z-index:9999;transition:transform .3s;white-space:nowrap;backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px)}
.toast.show{transform:translateX(-50%) translateY(0)}
.empty{text-align:center;padding:28px 16px;color:var(--faint);font-style:italic;font-size:15px}
/* ── APPS DRAWER ── */
.apps-trigger{background:none;border:none;cursor:pointer;color:rgba(239,236,229,0.55);
  font-size:16px;padding:8px 10px;min-width:36px;min-height:36px;display:flex;
  align-items:center;justify-content:center;transition:color .15s;line-height:1}
.apps-trigger:active{color:var(--steel-lt)}
.apps-overlay{position:fixed;inset:0;background:rgba(10,10,10,0.65);z-index:400;
  display:flex;align-items:flex-end;justify-content:center}
.apps-drawer{background:var(--surface);border-top:3px solid var(--steel);
  width:100%;max-width:430px;padding:0 0 calc(20px + var(--safe-bottom))}
.apps-drag{width:38px;height:3px;background:var(--border-strong);border-radius:999px;margin:12px auto 16px}
.apps-title{font-family:'Cormorant Garamond',serif;font-size:18px;font-weight:700;
  color:var(--ink);padding:0 20px 14px;border-bottom:1px solid var(--border);letter-spacing:.02em}
.apps-title span{font-family:'DM Mono',monospace;font-size:10px;letter-spacing:.08em;
  color:var(--faint);font-weight:400;margin-left:8px;vertical-align:middle}
.apps-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;padding:16px}
.app-tile{background:var(--surface);border:1.5px solid var(--border-strong);
  border-top:3px solid var(--border-strong);padding:14px 10px 12px;
  display:flex;flex-direction:column;align-items:center;gap:8px;
  cursor:pointer;text-decoration:none;transition:border-color .15s;min-height:88px;
  -webkit-tap-highlight-color:transparent}
.app-tile:active{border-color:var(--steel);background:var(--steel-faint)}
.app-tile.current{border-top-color:var(--steel);background:var(--steel-faint)}
.app-tile-icon{font-size:20px;line-height:1}
.app-tile-name{font-family:'DM Mono',monospace;font-size:9px;letter-spacing:.06em;
  text-transform:uppercase;color:var(--ink);text-align:center;line-height:1.3}
.app-tile-sub{font-family:'DM Mono',monospace;font-size:8px;color:var(--faint);
  text-align:center;line-height:1.3}
.app-tile.current .app-tile-name{color:var(--steel)}

@media(min-width:600px){
  .nav{display:flex;border-bottom:2px solid var(--border-strong);background:var(--ink);position:sticky;top:0;z-index:200}
  .nav-btn{flex:1;padding:13px 4px 11px;flex-direction:row;gap:0;min-height:auto;border-top:none;border-bottom:3px solid transparent;margin-bottom:-2px;font-size:9.5px}
  .nav-btn.active{border-top:none;border-bottom-color:var(--steel-lt)}
  .nav-icon{display:none}
  .bottom-nav{display:none}
  .app{padding-bottom:0}
  .toast{top:calc(var(--safe-top,0px) + 12px)}
}
`;

// ── TOP-LEVEL COMPONENTS ──────────────────────────────────────────────────────

function GradePicker({grade,setGrade,mod,setMod}){
  return(
    <div>
      <div className="gp-col-headers">{BUCKETS.map(b=><div key={b} className="gp-col-hdr" style={{color:BUCKET_COLOR[b].bg,opacity:.8}}>{b}</div>)}</div>
      <div className="gp-grid">
        {GRADES_ALL.map(({b,mod:m2,val})=>{
          const sel=grade===b&&mod===m2;
          return(
            <button key={b+m2} className="gp-btn"
              style={{background:sel?BUCKET_COLOR[b].bg:BUCKET_COLOR[b].light,color:sel?BUCKET_COLOR[b].text:BUCKET_COLOR[b].bg,border:`1.5px solid ${sel?BUCKET_COLOR[b].bg:"transparent"}`}}
              onClick={()=>{setGrade(b);setMod(m2);}}>
              <span className="gp-grade">{b}{m2}</span>
              <span className="gp-pts">{val.toFixed(2)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function WeightModal({initial,date,isEdit,onSave,onDelete,onClose,seedWeight}){
  const [weight,setWeight]=useState(initial??null);
  const [rawInput,setRawInput]=useState(initial!=null?String(initial):"");
  const [entryDate,setEntryDate]=useState(date??todayStr());
  const seed=seedWeight??185.0;
  const step=dir=>{
    const base=weight??seed;
    const next=Math.max(50,Math.min(999,Math.round((base+dir*0.2)*10)/10));
    setWeight(next);setRawInput(next.toFixed(1));
  };
  const handleChange=e=>{
    const v=e.target.value;setRawInput(v);
    if(v===""||v==="."){setWeight(null);return;}
    const n=parseFloat(v);
    if(!isNaN(n))setWeight(Math.max(50,Math.min(999,Math.round(n*10)/10)));
  };
  const handleBlur=()=>{ if(weight!=null)setRawInput(weight.toFixed(1)); else setRawInput(""); };
  const canSave=weight!=null;
  return(
    <div className="overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal">
        <div className="modal-drag"/>
        <div className="modal-title">{isEdit?"Edit Weight":"Log Weight"}<span className="modal-sub">{fmtFull(entryDate)}</span></div>
        <div className="w-stepper-wrap">
          <input className="inline-num weight-size" type="number" inputMode="decimal"
            value={rawInput} placeholder="—" onChange={handleChange} onBlur={handleBlur}
            onFocus={e=>e.target.select()} step={0.2}
            style={{color:weight==null?"var(--faint)":"var(--ink)"}}/>
          <div className="w-stepper-unit">{weight==null?"tap number or use buttons":"pounds · tap to edit"}</div>
          <div className="w-stepper-row">
            <button className="w-step-btn minus" onClick={()=>step(-1)} onPointerDown={e=>e.preventDefault()}>−</button>
            <div className="w-stepper-center"><span>0.2</span><span style={{fontSize:9,letterSpacing:".06em",opacity:.7}}>LBS</span></div>
            <button className="w-step-btn plus" onClick={()=>step(+1)} onPointerDown={e=>e.preventDefault()}>+</button>
          </div>
        </div>
        <div className="fm-row">
          <div className="fm-lbl">Date</div>
          <input className="fm-input" type="date" value={entryDate} onChange={e=>setEntryDate(e.target.value)}/>
        </div>
        <div className="fm-actions">
          {isEdit&&<button className="btn-danger" onClick={onDelete}>Delete</button>}
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={!canSave}
            style={{opacity:canSave?1:.45,cursor:canSave?"pointer":"not-allowed"}}
            onClick={()=>canSave&&onSave(weight,entryDate)}>
            {weight==null?"Enter weight to save":"Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NutModal({initial,date,isEdit,onSave,onDelete,onClose,seedWeight}){
  const recP=recProtein(seedWeight);const recC=recCalories(seedWeight);
  const [entryDate,setEntryDate]=useState(date??todayStr());
  const [protein,setProtein]=useState(initial?.protein??recP);
  const [calories,setCalories]=useState(initial?.calories??recC);
  const [water,setWater]=useState(initial?.water??8);
  return(
    <div className="overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal">
        <div className="modal-drag"/>
        <div className="modal-title">{isEdit?"Edit Nutrition":"Log Nutrition"}<span className="modal-sub">{fmtFull(entryDate)}</span></div>
        <div className="fm-row">
          <div className="fm-lbl-row">
            <span className="fm-lbl" style={{marginBottom:0}}>Protein</span>
            <button className="fm-rec" onClick={()=>setProtein(recP)}>Rec: {recP}g ↩</button>
          </div>
          <div style={{display:"flex",alignItems:"center"}}>
            <button className="fm-num-btn" onClick={()=>setProtein(v=>Math.max(0,v-1))}>−</button>
            <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",padding:"8px 0",borderTop:"1.5px solid var(--border-strong)",borderBottom:"1.5px solid var(--border-strong)"}}>
              <input className="inline-num nut-size" type="number" inputMode="numeric"
                value={protein} onChange={e=>{const n=parseInt(e.target.value);if(!isNaN(n))setProtein(Math.max(0,Math.min(500,n)));}}
                onFocus={e=>e.target.select()}/>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--faint)",letterSpacing:".06em"}}>g · {Math.round((protein/recP)*100)}% of rec</div>
            </div>
            <button className="fm-num-btn" onClick={()=>setProtein(v=>Math.min(500,v+1))}>+</button>
          </div>
        </div>
        <div className="fm-row">
          <div className="fm-lbl-row">
            <span className="fm-lbl" style={{marginBottom:0}}>Calories</span>
            <button className="fm-rec" onClick={()=>setCalories(recC)}>Rec: {recC} ↩</button>
          </div>
          <div style={{display:"flex",alignItems:"center"}}>
            <button className="fm-num-btn" onClick={()=>setCalories(v=>Math.max(0,v-10))}>−</button>
            <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",padding:"8px 0",borderTop:"1.5px solid var(--border-strong)",borderBottom:"1.5px solid var(--border-strong)"}}>
              <input className="inline-num nut-size" type="number" inputMode="numeric"
                value={calories} onChange={e=>{const n=parseInt(e.target.value);if(!isNaN(n))setCalories(Math.max(0,Math.min(6000,n)));}}
                onFocus={e=>e.target.select()}/>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--faint)",letterSpacing:".06em"}}>kcal · {Math.round((calories/recC)*100)}% of rec</div>
            </div>
            <button className="fm-num-btn" onClick={()=>setCalories(v=>Math.min(6000,v+10))}>+</button>
          </div>
        </div>
        <div className="water-wrap">
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
            <span className="fm-lbl" style={{marginBottom:0}}>Water</span>
            <button className="fm-rec" onClick={()=>setWater(8)}>8 glasses ↩</button>
          </div>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <div>
              <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:48,letterSpacing:".02em",color:"var(--steel)",lineHeight:1}}>{water}</span>
              <span style={{fontFamily:"'DM Mono',monospace",fontSize:11,color:"var(--faint)",marginLeft:6}}>{water===1?"glass":"glasses"}</span>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:11,color:"var(--ink)"}}>{water*8} oz</div>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--faint)"}}>{(water*236.6/1000).toFixed(1)} L</div>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",marginBottom:10}}>
            <button className="fm-num-btn" onClick={()=>setWater(v=>Math.max(0,v-1))}>−</button>
            <div style={{flex:1,height:50,background:"var(--surface)",border:"1.5px solid var(--border-strong)",borderLeft:"none",borderRight:"none",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Mono',monospace",fontSize:11,color:"var(--faint)",letterSpacing:".06em",textTransform:"uppercase"}}>8oz per glass</div>
            <button className="fm-num-btn" onClick={()=>setWater(v=>Math.min(16,v+1))}>+</button>
          </div>
          <div className="water-cells">
            {Array.from({length:16},(_,i)=>{
              const n=i+1,isFilled=n<=water,emptyIdx=n-water;
              return <div key={n} className={"water-cell "+(isFilled?"filled":"empty")} onClick={()=>setWater(n)}>{isFilled?n:emptyIdx}</div>;
            })}
          </div>
          <div style={{display:"flex",justifyContent:"space-between",marginTop:6}}>
            <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--faint)"}}>1</span>
            <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--steel)"}}>goal: 8</span>
            <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--faint)"}}>16</span>
          </div>
        </div>
        <div className="fm-row">
          <div className="fm-lbl">Date</div>
          <input className="fm-input" type="date" value={entryDate} onChange={e=>setEntryDate(e.target.value)}/>
        </div>
        <div className="fm-actions">
          {isEdit&&<button className="btn-danger" onClick={onDelete}>Delete</button>}
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={()=>onSave({protein,calories,water},entryDate)}>Save</button>
        </div>
      </div>
    </div>
  );
}

function WeightChart({entries,chartRange,setChartRange,onDotClick}){
  const [tooltip,setTooltip]=useState(null);
  const today=todayStr();
  const sorted=[...entries].sort((a,b)=>a.date.localeCompare(b.date));
  const slice=sorted.slice(-chartRange);
  const weights=slice.map(e=>e.weight).filter(Boolean);
  if(weights.length<2)return(
    <div>
      <div style={{display:"flex",justifyContent:"flex-end",gap:4,marginBottom:10}}>
        {[7,14,30].map(r=><button key={r} className={"chart-range-btn"+(chartRange===r?" active":"")} onClick={()=>setChartRange(r)}>{r}d</button>)}
      </div>
      <div className="empty" style={{fontSize:13}}>Log 2+ entries to see trend</div>
    </div>
  );
  const W=340,H=90,PAD=8;
  const minW=Math.min(...weights)-0.8,maxW=Math.max(...weights)+0.8;
  const xS=i=>PAD+(i/(slice.length-1))*(W-PAD*2);
  const yS=w=>H-PAD-((w-minW)/(maxW-minW||1))*(H-PAD*2);
  const pts=slice.map((e,i)=>e.weight?{x:xS(i),y:yS(e.weight),e}:null).filter(Boolean);
  const polyPts=pts.map(p=>`${p.x},${p.y}`).join(" ");
  const areaD=pts.length>1?`M ${pts[0].x},${pts[0].y} ${pts.slice(1).map(p=>`L ${p.x},${p.y}`).join(" ")} L ${pts[pts.length-1].x},${H} L ${pts[0].x},${H} Z`:"";
  return(
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
        <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:".12em",textTransform:"uppercase",color:"var(--faint)"}}>Weight Trend</span>
        <div style={{display:"flex",gap:4}}>{[7,14,30].map(r=><button key={r} className={"chart-range-btn"+(chartRange===r?" active":"")} onClick={()=>setChartRange(r)}>{r}d</button>)}</div>
      </div>
      <div style={{position:"relative"}}>
        <svg viewBox={`0 0 ${W} ${H}`} className="chart-svg">
          <defs>
            <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2B5C8A" stopOpacity=".5"/>
              <stop offset="100%" stopColor="#2B5C8A" stopOpacity="0"/>
            </linearGradient>
          </defs>
          {areaD&&<path className="chart-area" d={areaD}/>}
          <polyline className="chart-line" points={polyPts}/>
          {pts.map(({x,y,e})=>(
            <circle key={e.date} className={e.date===today?"chart-dot-today":"chart-dot"}
              cx={x} cy={y} r={e.date===today?4.5:3}
              onMouseEnter={()=>setTooltip({x,y,e})} onMouseLeave={()=>setTooltip(null)}
              onClick={()=>onDotClick(e.date)}/>
          ))}
          {[minW+0.5,(minW+maxW)/2,maxW-0.5].map(w=>(
            <text key={w} className="chart-axis-lbl" x={2} y={yS(w)+3}>{w.toFixed(1)}</text>
          ))}
        </svg>
        {tooltip&&(
          <div style={{position:"absolute",background:"var(--ink)",color:"var(--surface)",padding:"5px 9px",fontFamily:"'DM Mono',monospace",fontSize:10,letterSpacing:".04em",borderTop:"2px solid var(--steel)",whiteSpace:"nowrap",pointerEvents:"none",left:tooltip.x,top:Math.max(0,tooltip.y-34),transform:"translateX(-50%)"}}>
            {fmtDate(tooltip.e.date)} · {tooltip.e.weight?.toFixed(1)} lbs
          </div>
        )}
      </div>
    </div>
  );
}

function NutCard({label,val,target,unit,color,onClick}){
  const pct=target&&val!=null?Math.min(100,Math.round((val/target)*100)):0;
  return(
    <div className="nut-card" onClick={onClick}>
      <div className="nut-card-accent" style={{background:color}}/>
      <div className="nut-card-lbl">{label}</div>
      <div className="nut-card-val">{val??'—'}</div>
      <div className="nut-card-unit">{val!=null?unit:"not logged"}</div>
      {val!=null&&target&&(
        <>
          <div className="nut-card-prog"><div className="nut-card-prog-fill" style={{width:pct+"%",background:color}}/></div>
          <div className="nut-card-target">{pct}% of {target}{unit}</div>
        </>
      )}
    </div>
  );
}

function ActivityModal({initial,date,isEdit,onSave,onDelete,onClose}){
  const [entryDate,setEntryDate]=useState(date??todayStr());
  const [exercise,setExercise]=useState(initial?.exercise??null);
  const [steps,setSteps]=useState(initial?.steps??null);
  const [exRaw,setExRaw]=useState(initial?.exercise!=null?String(initial.exercise):"");
  const [stRaw,setStRaw]=useState(initial?.steps!=null?String(initial.steps):"");
  const exGrade=exercise!=null?ACT_EXERCISE_GRADE(exercise):null;
  const stGrade=steps!=null?ACT_STEPS_GRADE(steps):null;
  const canSave=exercise!=null||steps!=null;
  return(
    <div className="overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal">
        <div className="modal-drag"/>
        <div className="modal-title">{isEdit?"Edit Activity":"Log Activity"}<span className="modal-sub">{fmtFull(entryDate)}</span></div>
        {/* Exercise */}
        <div className="act-modal-section">
          <div className="act-modal-sec-lbl">Exercise Time</div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
            <button className="fm-num-btn" onClick={()=>{const v=Math.max(0,(exercise??0)-5);setExercise(v);setExRaw(String(v));}}>−</button>
            <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",padding:"8px 0",borderTop:"1.5px solid var(--border-strong)",borderBottom:"1.5px solid var(--border-strong)"}}>
              <input className="inline-num nut-size" type="number" inputMode="numeric" placeholder="—"
                value={exRaw} onChange={e=>{setExRaw(e.target.value);const n=parseInt(e.target.value);if(!isNaN(n)){setExercise(Math.max(0,n));}else if(e.target.value==="")setExercise(null);}}
                onFocus={e=>e.target.select()}
                style={{color:exercise==null?"var(--faint)":"var(--ink)"}}/>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--faint)",letterSpacing:".06em"}}>
                minutes{exGrade&&<span style={{marginLeft:6,color:ACT_GRADE_COLOR[exGrade],fontWeight:600}}>→ Grade {exGrade}</span>}
              </div>
            </div>
            <button className="fm-num-btn" onClick={()=>{const v=(exercise??0)+5;setExercise(v);setExRaw(String(v));}}>+</button>
          </div>
          <div style={{display:"flex",gap:6}}>
            {ACT_EXERCISE_RANGES.map(({grade,label,min})=>(
              <button key={grade} onClick={()=>{setExercise(min===0?0:min);setExRaw(String(min===0?0:min));}}
                style={{flex:1,padding:"6px 4px",border:"1.5px solid "+(exGrade===grade?ACT_GRADE_COLOR[grade]:"var(--border-strong)"),background:exGrade===grade?ACT_GRADE_COLOR[grade]:"none",color:exGrade===grade?"var(--surface)":"var(--muted)",fontFamily:"'DM Mono',monospace",fontSize:9,cursor:"pointer",letterSpacing:".04em",textAlign:"center"}}>
                <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:16}}>{grade}</div>
                <div style={{fontSize:8,marginTop:1,opacity:.8}}>{label}</div>
              </button>
            ))}
          </div>
        </div>
        {/* Steps */}
        <div className="act-modal-section">
          <div className="act-modal-sec-lbl">Steps</div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
            <button className="fm-num-btn" onClick={()=>{const v=Math.max(0,(steps??0)-500);setSteps(v);setStRaw(String(v));}}>−</button>
            <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",padding:"8px 0",borderTop:"1.5px solid var(--border-strong)",borderBottom:"1.5px solid var(--border-strong)"}}>
              <input className="inline-num nut-size" type="number" inputMode="numeric" placeholder="—"
                value={stRaw} onChange={e=>{setStRaw(e.target.value);const n=parseInt(e.target.value);if(!isNaN(n)){setSteps(Math.max(0,n));}else if(e.target.value==="")setSteps(null);}}
                onFocus={e=>e.target.select()}
                style={{color:steps==null?"var(--faint)":"var(--ink)"}}/>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--faint)",letterSpacing:".06em"}}>
                steps{stGrade&&<span style={{marginLeft:6,color:ACT_GRADE_COLOR[stGrade],fontWeight:600}}>→ Grade {stGrade}</span>}
              </div>
            </div>
            <button className="fm-num-btn" onClick={()=>{const v=(steps??0)+500;setSteps(v);setStRaw(String(v));}}>+</button>
          </div>
          <div style={{display:"flex",gap:6}}>
            {ACT_STEPS_RANGES.map(({grade,label,min})=>(
              <button key={grade} onClick={()=>{setSteps(min);setStRaw(String(min));}}
                style={{flex:1,padding:"6px 4px",border:"1.5px solid "+(stGrade===grade?ACT_GRADE_COLOR[grade]:"var(--border-strong)"),background:stGrade===grade?ACT_GRADE_COLOR[grade]:"none",color:stGrade===grade?"var(--surface)":"var(--muted)",fontFamily:"'DM Mono',monospace",fontSize:9,cursor:"pointer",letterSpacing:".04em",textAlign:"center"}}>
                <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:16}}>{grade}</div>
                <div style={{fontSize:8,marginTop:1,opacity:.8,lineHeight:1.2}}>{label}</div>
              </button>
            ))}
          </div>
        </div>
        <div className="fm-row">
          <div className="fm-lbl">Date</div>
          <input className="fm-input" type="date" value={entryDate} onChange={e=>setEntryDate(e.target.value)}/>
        </div>
        <div className="fm-actions">
          {isEdit&&<button className="btn-danger" onClick={onDelete}>Delete</button>}
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={!canSave} style={{opacity:canSave?1:.45,cursor:canSave?"pointer":"not-allowed"}}
            onClick={()=>canSave&&onSave({exercise,steps},entryDate)}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function Fitnessgrade(){
  const [meals,setMeals]=useState(()=>{try{return JSON.parse(localStorage.getItem("fg4_meals")||"null")||MEAL_SEED;}catch{return MEAL_SEED;}});
  const [dashEntries,setDashEntries]=useState(()=>{try{return JSON.parse(localStorage.getItem("fg_weight")||"null")||[];}catch{return [];}});
  const [tab,setTab]=useState("home");
  const [appsOpen,setAppsOpen]=useState(false);
  const [actEntries,setActEntries]=useState(()=>{try{return JSON.parse(localStorage.getItem("fg_act")||"null")||[];}catch{return [];}});
  const [actModal,setActModal]=useState(null);
  const [actCalYear,setActCalYear]=useState(new Date().getFullYear());
  const [actCalMonth,setActCalMonth]=useState(new Date().getMonth());
  const [actSelDate,setActSelDate]=useState(todayStr());

  // meal calendar
  const nowD=new Date();
  const [calYear,setCalYear]=useState(nowD.getFullYear());
  const [calMonth,setCalMonth]=useState(nowD.getMonth());
  const [selDate,setSelDate]=useState(todayStr());

  // body calendar
  const [dashCalYear,setDashCalYear]=useState(nowD.getFullYear());
  const [dashCalMonth,setDashCalMonth]=useState(nowD.getMonth());
  const [dashSelDate,setDashSelDate]=useState(todayStr());
  const [chartRange,setChartRange]=useState(14);

  // meal log modal
  const [modal,setModal]=useState(null);
  const [mGrade,setMGrade]=useState("A");
  const [mMod,setMMod]=useState("");
  const [mName,setMName]=useState("");
  const [mDate,setMDate]=useState(todayStr());

  // library
  const [libSearch,setLibSearch]=useState("");
  const [libBucketFilter,setLibBucketFilter]=useState("ALL");
  const [libSort,setLibSort]=useState("name_asc");
  const [libExpanded,setLibExpanded]=useState(null);
  const [libModal,setLibModal]=useState(null);
  const [lmGrade,setLmGrade]=useState("A");
  const [lmMod,setLmMod]=useState("");
  const [lmName,setLmName]=useState("");
  const searchRef=useRef(null);

  // body modals
  const [wModal,setWModal]=useState(null);
  const [nModal,setNModal]=useState(null);

  // toast
  const [toastMsg,setToastMsg]=useState("");
  const [toastVis,setToastVis]=useState(false);

  useEffect(()=>{document.title="Fitnessgrade — Track what you eat. Grade it.";},[]);
  useEffect(()=>{try{localStorage.setItem("fg4_meals",JSON.stringify(meals));}catch{}},[meals]);
  useEffect(()=>{try{localStorage.setItem("fg_weight",JSON.stringify(dashEntries));}catch{}},[dashEntries]);
  useEffect(()=>{try{localStorage.setItem("fg_act",JSON.stringify(actEntries));}catch{}},[actEntries]);

  const toast=useCallback((msg)=>{setToastMsg(msg);setToastVis(true);setTimeout(()=>setToastVis(false),10000);},[]);

  // ── MEAL CRUD ──
  function openAdd(date){setMGrade("A");setMMod("");setMName("");setMDate(date||todayStr());setModal({mode:"add"});}
  function openEdit(meal){setMGrade(meal.bucket);setMMod(meal.mod);setMName(meal.name||"");setMDate(meal.date);setModal({mode:"edit",meal});}
  function openAddFromLib(entry){setMGrade(entry.bucket);setMMod(entry.mod);setMName(entry.name||"");setMDate(todayStr());setModal({mode:"add"});}
  function saveMeal(){
    if(modal.mode==="add"){
      setMeals(p=>[{id:uid(),bucket:mGrade,mod:mMod,name:mName.trim()||null,date:mDate,ts:Date.now()},...p]);
      toast(`Logged ${mGrade}${mMod} · ${gradeVal(mGrade,mMod).toFixed(2)} pts`);
    } else {
      setMeals(p=>p.map(m=>m.id===modal.meal.id?{...m,bucket:mGrade,mod:mMod,name:mName.trim()||null,date:mDate}:m));
      toast("Meal updated");
    }
    setModal(null);
  }
  function deleteMealById(id){setMeals(p=>p.filter(m=>m.id!==id));setModal(null);toast("Meal deleted");}

  // ── LIBRARY CRUD ──
  const libraryEntries=useMemo(()=>{
    const map={};
    meals.forEach(m=>{
      if(!m.name||!["A","B","C"].includes(m.bucket))return;
      const key=`${m.bucket}|${m.mod}|${m.name.trim().toLowerCase()}`;
      if(!map[key])map[key]={key,bucket:m.bucket,mod:m.mod,name:m.name.trim(),instances:[],lastDate:"",lastTs:0};
      map[key].instances.push(m);
      if(m.ts>map[key].lastTs){map[key].lastTs=m.ts;map[key].lastDate=m.date;}
    });
    return Object.values(map);
  },[meals]);

  const filteredLib=useMemo(()=>{
    let list=[...libraryEntries];
    if(libBucketFilter!=="ALL")list=list.filter(e=>e.bucket===libBucketFilter);
    if(libSearch.trim()){const q=libSearch.trim().toLowerCase();list=list.filter(e=>e.name.toLowerCase().includes(q));}
    list.sort((a,b)=>{
      switch(libSort){
        case"name_asc":return a.name.localeCompare(b.name);case"name_desc":return b.name.localeCompare(a.name);
        case"grade_desc":return gradeVal(b.bucket,b.mod)-gradeVal(a.bucket,a.mod);case"grade_asc":return gradeVal(a.bucket,a.mod)-gradeVal(b.bucket,b.mod);
        case"freq_desc":return b.instances.length-a.instances.length;case"recent":return b.lastTs-a.lastTs;
        default:return 0;
      }
    });
    return list;
  },[libraryEntries,libBucketFilter,libSearch,libSort]);

  const groupedLib=useMemo(()=>{
    if(libBucketFilter!=="ALL"||libSearch.trim())return null;
    const g={A:[],B:[],C:[]};
    filteredLib.forEach(e=>{if(g[e.bucket])g[e.bucket].push(e);});
    return g;
  },[filteredLib,libBucketFilter,libSearch]);

  function openLibAdd(){setLmGrade("A");setLmMod("");setLmName("");setLibModal({mode:"add"});}
  function openLibEdit(entry){setLmGrade(entry.bucket);setLmMod(entry.mod);setLmName(entry.name);setLibModal({mode:"edit",entry});}
  function saveLibEntry(){
    if(!lmName.trim()){toast("Enter a meal name");return;}
    if(libModal.mode==="add"){
      setMeals(p=>[{id:uid(),bucket:lmGrade,mod:lmMod,name:lmName.trim(),date:todayStr(),ts:Date.now()},...p]);
      toast(`"${lmName.trim()}" added to library`);
    } else {
      const {entry}=libModal;
      const eKey=`${entry.bucket}|${entry.mod}|${entry.name.toLowerCase()}`;
      setMeals(p=>p.map(m=>{const k=`${m.bucket}|${m.mod}|${(m.name||"").trim().toLowerCase()}`;return k===eKey?{...m,bucket:lmGrade,mod:lmMod,name:lmName.trim()}:m;}));
      toast("Library entry updated");
    }
    setLibModal(null);
  }
  function deleteLibEntry(entry){
    const eKey=`${entry.bucket}|${entry.mod}|${entry.name.toLowerCase()}`;
    setMeals(p=>p.filter(m=>`${m.bucket}|${m.mod}|${(m.name||"").trim().toLowerCase()}`!==eKey));
    setLibModal(null);setLibExpanded(null);toast("Removed from library");
  }
  function deleteInstance(id){setMeals(p=>p.filter(m=>m.id!==id));toast("Log entry deleted");}
  function toggleExpand(key){setLibExpanded(prev=>prev===key?null:key);}

  // ── BODY CRUD ──
  const dashByDate=useMemo(()=>{const m={};dashEntries.forEach(e=>{m[e.date]=e;});return m;},[dashEntries]);
  const sortedDash=useMemo(()=>[...dashEntries].sort((a,b)=>b.date.localeCompare(a.date)),[dashEntries]);
  const latestEntry=sortedDash[0];
  const prevWeight=latestEntry?.weight??null;
  const todayDashEntry=dashByDate[todayStr()];

  const dashStreak=useMemo(()=>{let s=0;const dt=new Date();while(true){const ds=dt.toISOString().slice(0,10);if(!dashByDate[ds])break;s++;dt.setDate(dt.getDate()-1);}return s;},[dashByDate]);
  const wDelta=useMemo(()=>{const t=todayStr();const prev=sortedDash.find(e=>e.date<t&&e.weight);if(!prev||!todayDashEntry?.weight)return null;return Math.round((todayDashEntry.weight-prev.weight)*10)/10;},[sortedDash,todayDashEntry]);

  function saveDashEntry(data){setDashEntries(data);}
  function addOrUpdateWeight(weight,date){
    const ex=dashByDate[date];
    if(ex)saveDashEntry(dashEntries.map(e=>e.date===date?{...e,weight}:e));
    else saveDashEntry([...dashEntries,{id:uid(),date,weight,protein:null,calories:null,water:null}]);
    setWModal(null);toast(`Weight ${weight.toFixed(1)} lbs saved`);
  }
  function deleteWeightEntry(date){
    const e=dashByDate[date];if(!e)return;
    if(e.protein!=null||e.calories!=null||e.water!=null)saveDashEntry(dashEntries.map(en=>en.date===date?{...en,weight:undefined}:en));
    else saveDashEntry(dashEntries.filter(en=>en.date!==date));
    setWModal(null);toast("Weight deleted");
  }
  function addOrUpdateNutrition({protein,calories,water},date){
    const ex=dashByDate[date];
    if(ex)saveDashEntry(dashEntries.map(e=>e.date===date?{...e,protein,calories,water}:e));
    else saveDashEntry([...dashEntries,{id:uid(),date,protein,calories,water,weight:undefined}]);
    setNModal(null);toast("Nutrition saved");
  }
  function deleteNutritionEntry(date){
    const e=dashByDate[date];if(!e)return;
    if(e.weight!=null)saveDashEntry(dashEntries.map(en=>en.date===date?{...en,protein:null,calories:null,water:null}:en));
    else saveDashEntry(dashEntries.filter(en=>en.date!==date));
    setNModal(null);toast("Nutrition deleted");
  }

  // ── ACTIVITY CRUD ──
  const actByDate=useMemo(()=>{const m={};actEntries.forEach(e=>{m[e.date]=e;});return m;},[actEntries]);
  const sortedAct=useMemo(()=>[...actEntries].sort((a,b)=>b.date.localeCompare(a.date)),[actEntries]);
  const todayAct=actByDate[todayStr()];
  const actCalDays=useMemo(()=>calDaysFor(actCalYear,actCalMonth),[actCalYear,actCalMonth]);

  function addOrUpdateActivity({exercise,steps},date){
    const ex=actByDate[date];
    if(ex)setActEntries(actEntries.map(e=>e.date===date?{...e,exercise,steps}:e));
    else setActEntries([...actEntries,{id:uid(),date,exercise,steps}]);
    setActModal(null);toast("Activity saved");
  }
  function deleteActivity(date){
    setActEntries(actEntries.filter(e=>e.date!==date));
    setActModal(null);toast("Activity deleted");
  }

  // ── DERIVED / CALENDAR ──
  const today=todayStr();
  const mealsByDate=useMemo(()=>{const m={};meals.forEach(x=>{if(!m[x.date])m[x.date]=[];m[x.date].push(x);});return m;},[meals]);
  const dayGPA=ds=>{const ms=mealsByDate[ds];return ms?.length?avg(ms.map(m=>gradeVal(m.bucket,m.mod))):null;};
  const selMeals=mealsByDate[selDate]||[];
  const selGPA=dayGPA(selDate);
  const todayMeals=mealsByDate[today]||[];

  // GPA = average of per-day GPAs blending meals + activity (each day weighted equally)
  // Activity contributes: exercise grade + steps grade averaged into one activity GPA per day
  // Daily GPA = avg(all meal grades + activity grades for that day)
  const dailyAvgsByDate=useMemo(()=>{
    const out={};
    // Collect all dates that have meals or activity
    const allDates=new Set([
      ...Object.keys(mealsByDate),
      ...actEntries.map(e=>e.date),
    ]);
    allDates.forEach(date=>{
      const mealGrades=(mealsByDate[date]||[]).map(m=>gradeVal(m.bucket,m.mod));
      const actEntry=actByDate[date];
      const actGrades=[];
      if(actEntry?.exercise!=null) actGrades.push(gradeVal(ACT_EXERCISE_GRADE(actEntry.exercise),""));
      if(actEntry?.steps!=null)    actGrades.push(gradeVal(ACT_STEPS_GRADE(actEntry.steps),""));
      const all=[...mealGrades,...actGrades];
      const g=avg(all);
      if(g!=null)out[date]=g;
    });
    return out;
  },[mealsByDate,actByDate,actEntries]);

  const periodGPA=useCallback((filterFn)=>{
    const vals=Object.entries(dailyAvgsByDate).filter(([date])=>filterFn(date)).map(([,g])=>g);
    return avg(vals);
  },[dailyAvgsByDate]);

  const ws=useMemo(()=>{const d=new Date(today);d.setDate(d.getDate()-d.getDay());return d.toISOString().slice(0,10);},[today]);

  // Today GPA includes meal grades + activity grades
  const todayActEntry=actByDate[today];
  const todayActGrades=[];
  if(todayActEntry?.exercise!=null) todayActGrades.push(gradeVal(ACT_EXERCISE_GRADE(todayActEntry.exercise),""));
  if(todayActEntry?.steps!=null)    todayActGrades.push(gradeVal(ACT_STEPS_GRADE(todayActEntry.steps),""));
  const dailyGPA=avg([...todayMeals.map(m=>gradeVal(m.bucket,m.mod)),...todayActGrades]);
  const weeklyGPA=useMemo(()=>periodGPA(date=>{const d=new Date(date);d.setDate(d.getDate()-d.getDay());return d.toISOString().slice(0,10)===ws;}),[periodGPA,ws]);
  const monthlyGPA=useMemo(()=>periodGPA(date=>date.slice(0,7)===today.slice(0,7)),[periodGPA,today]);
  const quarterGPA=useMemo(()=>periodGPA(date=>{const mo=parseInt(date.slice(5,7)),tmo=parseInt(today.slice(5,7));return date.slice(0,4)===today.slice(0,4)&&Math.ceil(mo/3)===Math.ceil(tmo/3);}),[periodGPA,today]);
  const annualGPA=useMemo(()=>periodGPA(date=>date.slice(0,4)===today.slice(0,4)),[periodGPA,today]);
  const allTimeGPA=useMemo(()=>avg(Object.values(dailyAvgsByDate)),[dailyAvgsByDate]);

  // meal counts per period (for stats display)
  const weekMeals=useMemo(()=>meals.filter(m=>{const d=new Date(m.date);d.setDate(d.getDate()-d.getDay());return d.toISOString().slice(0,10)===ws;}),[meals,ws]);
  const monthMeals=useMemo(()=>meals.filter(m=>m.date.slice(0,7)===today.slice(0,7)),[meals,today]);
  const cheatMeals=useMemo(()=>meals.filter(m=>m.bucket==="D"),[meals]);
  const dist=BUCKETS.map(b=>({b,count:meals.filter(m=>m.bucket===b).length}));
  const total=meals.length||1;
  const calDays=useMemo(()=>calDaysFor(calYear,calMonth),[calYear,calMonth]);
  const dashCalDays=useMemo(()=>calDaysFor(dashCalYear,dashCalMonth),[dashCalYear,dashCalMonth]);

  // ── RENDER HELPERS ──
  const renderMI=(m,showDate=false)=>(
    <div className="mi" key={m.id}>
      <div className="mi-badge" style={{background:BUCKET_COLOR[m.bucket].light,color:BUCKET_COLOR[m.bucket].bg}} onClick={()=>openEdit(m)}>{m.bucket}{m.mod}</div>
      <div className="mi-info" onClick={()=>openEdit(m)}>
        <div className="mi-name">{m.name||<span style={{fontStyle:"italic",opacity:.5}}>Unnamed</span>}</div>
        <div className="mi-meta">{showDate&&<>{fmtDate(m.date)} · </>}{gradeVal(m.bucket,m.mod).toFixed(2)} pts{m.bucket==="D"&&<> · <span className="cheat-tag">CHEAT</span></>}</div>
      </div>
      <div className="mi-acts">
        <button className="icon-btn" onClick={()=>openEdit(m)}>✎</button>
        <button className="icon-btn" onClick={()=>deleteMealById(m.id)}>×</button>
      </div>
    </div>
  );

  const renderLibCard=entry=>{
    const bc=BUCKET_COLOR[entry.bucket];const expanded=libExpanded===entry.key;
    return(
      <div className="lib-card" key={entry.key}>
        <div className="lib-card-main">
          <div className="lib-card-badge" style={{background:bc.light,color:bc.bg}}>{entry.bucket}{entry.mod}</div>
          <div className="lib-card-info">
            <div className="lib-card-name">{entry.name}</div>
            <div className="lib-card-meta">
              <span className="lib-card-pts">{gradeVal(entry.bucket,entry.mod).toFixed(2)} pts</span>
              <span className="lib-card-freq">×{entry.instances.length}</span>
              {entry.lastDate&&<span className="lib-card-last">Last: {fmtDate(entry.lastDate)}</span>}
            </div>
          </div>
          <div className="lib-card-acts">
            <button className="lib-relog-btn" onClick={()=>openAddFromLib(entry)}>+ Relog</button>
            <div className="lib-card-icon-acts">
              <button className="lib-icon-btn" onClick={()=>openLibEdit(entry)}>✎</button>
              <button className="lib-icon-btn" onClick={()=>toggleExpand(entry.key)} style={{fontSize:12}}>{expanded?"▲":"▼"}</button>
            </div>
          </div>
        </div>
        {expanded&&(
          <div className="lib-card-expand">
            <div className="fm-lbl" style={{marginBottom:8}}>Log History</div>
            {[...entry.instances].sort((a,b)=>b.ts-a.ts).map(m=>(
              <div className="lib-hist-row" key={m.id}>
                <span className="lib-hist-date">{fmtDate(m.date)}</span>
                <span className="lib-hist-grade" style={{color:bc.bg}}>{m.bucket}{m.mod}</span>
                <button className="lib-hist-del" onClick={()=>deleteInstance(m.id)}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderLibSection=()=>{
    if(groupedLib){
      const anyEntry=Object.values(groupedLib).some(a=>a.length>0);
      if(!anyEntry)return<div className="lib-empty">No library entries yet.<br/><span style={{fontSize:13}}>Log named A, B, or C meals to build your library.</span></div>;
      return["A","B","C"].map(b=>{
        const entries=groupedLib[b];if(!entries.length)return null;
        return(
          <div key={b}>
            <div className="lib-bucket-divider">
              <div className="lib-buc-badge" style={{background:BUCKET_COLOR[b].bg,color:BUCKET_COLOR[b].text}}>{b}</div>
              <div className="lib-buc-label">{BUCKET_LABEL[b]}</div>
              <div className="lib-buc-count">{entries.length} meal{entries.length!==1?"s":""}</div>
            </div>
            {entries.map(renderLibCard)}
          </div>
        );
      });
    }
    if(!filteredLib.length)return<div className="lib-empty">No results for "{libSearch}"</div>;
    return filteredLib.map(renderLibCard);
  };

  // log modal
  const renderLogModal=()=>{
    if(!modal)return null;const isEdit=modal.mode==="edit";
    return(
      <div className="overlay" onClick={e=>e.target===e.currentTarget&&setModal(null)}>
        <div className="modal">
          <div style={{display:"flex",alignItems:"center",padding:"10px 16px 8px",borderBottom:"1px solid var(--border)"}}>
            <div style={{width:32,height:3,background:"var(--border-strong)",borderRadius:999,marginRight:12,flexShrink:0}}/>
            <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:17,fontWeight:700,color:"var(--ink)",flex:1}}>{isEdit?"Edit Meal":"Log a Meal"}</div>
          </div>
          <div style={{padding:"8px 16px 6px",borderBottom:"1px solid var(--border)"}}>
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:8.5,letterSpacing:".12em",textTransform:"uppercase",color:"var(--faint)",marginBottom:6}}>Grade</div>
            <GradePicker grade={mGrade} setGrade={setMGrade} mod={mMod} setMod={setMMod}/>
          </div>
          <div style={{padding:"8px 16px",borderBottom:"1px solid var(--border)",display:"flex",flexDirection:"column",gap:6}}>
            <input className="fm-input" style={{padding:"9px 12px",fontSize:15}} placeholder="Meal name (optional)…"
              value={mName} onChange={e=>setMName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&saveMeal()} autoFocus={!isEdit}/>
            <input className="fm-input" type="date" style={{padding:"7px 12px",fontSize:14}} value={mDate} onChange={e=>setMDate(e.target.value)}/>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 16px",borderBottom:"1px solid var(--border)",background:"var(--steel-faint)"}}>
            <div style={{width:36,height:36,borderRadius:3,background:BUCKET_COLOR[mGrade].bg,color:BUCKET_COLOR[mGrade].text,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Cormorant Garamond',serif",fontSize:15,fontWeight:700,flexShrink:0}}>{mGrade}{mMod}</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:14,fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{mName||<span style={{fontStyle:"italic",opacity:.45}}>Unnamed</span>}</div>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--muted)"}}>{gradeVal(mGrade,mMod).toFixed(2)} pts · {fmtDate(mDate)}{mGrade==="D"&&<> · <span className="cheat-tag">CHEAT</span></>}</div>
            </div>
          </div>
          <div style={{display:"flex",gap:8,padding:"10px 16px"}}>
            {isEdit&&<button className="btn-danger" style={{padding:"11px 14px",minHeight:44}} onClick={()=>deleteMealById(modal.meal.id)}>Delete</button>}
            <button className="btn-ghost" style={{padding:"11px 14px",minHeight:44}} onClick={()=>setModal(null)}>Cancel</button>
            <button className="btn-primary" style={{minHeight:44}} onClick={saveMeal}>{isEdit?"Save":`Log ${mGrade}${mMod}`}</button>
          </div>
        </div>
      </div>
    );
  };

  // lib modal
  const renderLibModal=()=>{
    if(!libModal)return null;const isEdit=libModal.mode==="edit";const entry=libModal.entry;
    return(
      <div className="overlay" onClick={e=>e.target===e.currentTarget&&setLibModal(null)}>
        <div className="modal">
          <div className="modal-drag"/>
          <div className="modal-title">{isEdit?"Edit Library Entry":"Add to Library"}{isEdit&&<span className="modal-sub">{entry.instances.length} log{entry.instances.length!==1?"s":""}</span>}</div>
          <div className="fm-row">
            <div className="fm-lbl">Grade</div>
            <GradePicker grade={lmGrade} setGrade={setLmGrade} mod={lmMod} setMod={setLmMod}/>
          </div>
          <div className="fm-row">
            <div className="fm-lbl">Meal Name <span style={{opacity:.5,fontWeight:400}}>(required)</span></div>
            <input className="fm-input" placeholder="e.g. Grilled salmon + quinoa…" value={lmName} onChange={e=>setLmName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&saveLibEntry()} autoFocus/>
          </div>
          <div className="lm-preview">
            <div className="lm-prev-badge" style={{background:BUCKET_COLOR[lmGrade].bg,color:BUCKET_COLOR[lmGrade].text}}>{lmGrade}{lmMod}</div>
            <div>
              <div className="lm-prev-name">{lmName||<span style={{fontStyle:"italic",opacity:.4}}>Name required</span>}</div>
              <div className="lm-prev-meta">{lmGrade}{lmMod} · {gradeVal(lmGrade,lmMod).toFixed(2)} pts{isEdit?` · renames all ${entry.instances.length} logs`:""}</div>
            </div>
          </div>
          <div className="fm-actions">
            {isEdit&&<button className="btn-danger" onClick={()=>deleteLibEntry(entry)}>Delete All</button>}
            <button className="btn-ghost" onClick={()=>setLibModal(null)}>Cancel</button>
            <button className="btn-primary" onClick={saveLibEntry}>{isEdit?"Save Changes":"Add to Library"}</button>
          </div>
        </div>
      </div>
    );
  };

  return(
    <>
      <style>{CSS}</style>
      <div className="app">

        <nav className="nav">
          {[["home","Home"],["calendar","Calendar"],["analytics","Stats"],["library","Library"],["dashboard","Body"],["activity","Activity"]].map(([k,l])=>(
            <button key={k} className={"nav-btn"+(tab===k?" active":"")} onClick={()=>setTab(k)}>{l}</button>
          ))}
          <button className="nav-btn" onClick={()=>setAppsOpen(true)} style={{letterSpacing:'.08em'}}>⊞ Apps</button>
        </nav>

        {/* ══ HOME ══ */}
        {tab==="home"&&<div className="page-scroll">
          <div className="ph"><div className="ph-title">Fitnessgrade</div><div className="ph-sub">Track what you eat. Grade it.</div></div>
          <div className="hero">
            <div className="hero-lbl">Today's GPA</div>
            <div className="hero-val">{gpaFmt(dailyGPA)}</div>
            <div className="hero-ltr">{dailyGPA!=null?letterGrade(dailyGPA):"No meals yet"}</div>
            <div className="hero-row">
              <div className="hero-sub"><div className="hero-sub-lbl">Week</div><div className="hero-sub-val">{gpaFmt(weeklyGPA)}</div></div>
              <div className="hero-sub"><div className="hero-sub-lbl">Month</div><div className="hero-sub-val">{gpaFmt(monthlyGPA)}</div></div>
              <div className="hero-sub"><div className="hero-sub-lbl">Cheats</div><div className="hero-sub-val">{cheatMeals.length}</div></div>
            </div>
          </div>
          <div className="sec">
            <div className="sec-lbl">Quick Log</div>
            <div className="ql-col-headers">{BUCKETS.map(b=><div key={b} className="ql-col-hdr" style={{color:BUCKET_COLOR[b].bg,opacity:.75}}>{b}</div>)}</div>
            <div className="ql-grid">
              {GRADES_ALL.map(({b,mod,val})=>(
                <button key={b+mod} className="ql-btn"
                  style={{background:mod===""?BUCKET_COLOR[b].bg:BUCKET_COLOR[b].light,color:mod===""?BUCKET_COLOR[b].text:BUCKET_COLOR[b].bg}}
                  onClick={()=>{setMGrade(b);setMMod(mod);setMName("");setMDate(todayStr());setModal({mode:"add"});}}>
                  <span className="ql-grade">{b}{mod}</span>
                  <span className="ql-pts">{val.toFixed(2)}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="sec-lbl" style={{padding:"0 16px 8px"}}>Today's Meals</div>
          {todayMeals.length===0&&<div className="empty">No meals logged today<br/><span style={{fontSize:12}}>Tap a grade above to start</span></div>}
          {todayMeals.map(m=>renderMI(m))}
          <div style={{height:16}}/>
        </div>}

        {/* ══ CALENDAR ══ */}
        {tab==="calendar"&&<div className="page-scroll">
          <div className="ph"><div className="ph-title">Calendar</div><div className="ph-sub">Tap a day to view or log meals</div></div>
          <div className="cal-nav">
            <button className="cal-arrow" onClick={()=>{if(calMonth===0){setCalMonth(11);setCalYear(y=>y-1);}else setCalMonth(m=>m-1);}}>‹</button>
            <div className="cal-month">{MONTHS[calMonth]} {calYear}</div>
            <button className="cal-arrow" onClick={()=>{if(calMonth===11){setCalMonth(0);setCalYear(y=>y+1);}else setCalMonth(m=>m+1);}}>›</button>
          </div>
          <div className="cal-grid">
            {DOW.map(d=><div key={d} className="cal-dow">{d}</div>)}
            {calDays.map(({ds,cur})=>{
              const dm=mealsByDate[ds]||[];const gpa=dayGPA(ds);const isToday=ds===today;const isSel=ds===selDate;
              return(
                <div key={ds} className={"cal-day"+(!cur?" other-month":"")+(isToday?" today":"")+(isSel?" selected":"")} onClick={()=>setSelDate(ds)}>
                  <span className="cal-dn">{parseInt(ds.slice(8))}</span>
                  {gpa!=null&&<div className="cal-day-gpa">{gpa.toFixed(1)}</div>}
                  <div className="cal-dots">{dm.slice(0,4).map(m=><div key={m.id} className="cal-dot" style={{background:isSel?"rgba(247,245,240,.8)":BUCKET_COLOR[m.bucket].bg}}/>)}</div>
                </div>
              );
            })}
          </div>
          <div className="day-panel">
            <div className="dp-header">
              <div>
                <div className="dp-date">{fmtDate(selDate)}{selDate===today?" · Today":""}</div>
                {selGPA!=null&&<div className="dp-gpa">{gpaFmt(selGPA)} GPA · {letterGrade(selGPA)}</div>}
              </div>
              <button className="dp-add" onClick={()=>openAdd(selDate)}>+ Log</button>
            </div>
            {selMeals.length===0?<div className="dp-empty">No meals on this day</div>:selMeals.map(m=>renderMI(m))}
          </div>
          <div style={{height:16}}/>
        </div>}

        {/* ══ ANALYTICS ══ */}
        {tab==="analytics"&&<div className="page-scroll">
          <div className="ph"><div className="ph-title">Analytics</div><div className="ph-sub">Your nutrition GPA over time</div></div>
          <div className="stat-grid">
            {(()=>{
              const today2=todayStr();
              const ws2=ws;
              const daysLogged=date=>Object.keys(dailyAvgsByDate).filter(date=>date).length;
              const rows=[
                {lbl:"Today",   val:dailyGPA,   days:todayMeals.length>0?1:0,   meals:todayMeals.length},
                {lbl:"This Week",val:weeklyGPA,  days:Object.keys(dailyAvgsByDate).filter(d=>{const dt=new Date(d);dt.setDate(dt.getDate()-dt.getDay());return dt.toISOString().slice(0,10)===ws2;}).length, meals:weekMeals.length},
                {lbl:"This Month",val:monthlyGPA,days:Object.keys(dailyAvgsByDate).filter(d=>d.slice(0,7)===today2.slice(0,7)).length, meals:monthMeals.length},
                {lbl:"Quarter",val:quarterGPA,  days:Object.keys(dailyAvgsByDate).filter(d=>{const mo=parseInt(d.slice(5,7)),tmo=parseInt(today2.slice(5,7));return d.slice(0,4)===today2.slice(0,4)&&Math.ceil(mo/3)===Math.ceil(tmo/3);}).length, meals:null},
                {lbl:"This Year",val:annualGPA,  days:Object.keys(dailyAvgsByDate).filter(d=>d.slice(0,4)===today2.slice(0,4)).length, meals:null},
                {lbl:"All‑Time", val:allTimeGPA, days:Object.keys(dailyAvgsByDate).length, meals:null},
              ];
              return rows.map(({lbl,val,days,meals:mc})=>(
                <div className="sc" key={lbl}>
                  <div className="sc-lbl">{lbl}</div>
                  <div className="sc-val">{gpaFmt(val)}</div>
                  <div className="sc-ltr">{val!=null?letterGrade(val):"—"}</div>
                  {days>0&&<div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--faint)",marginTop:4,letterSpacing:".04em"}}>{days} day{days!==1?"s":""}{mc!=null?` · ${mc} meal${mc!==1?"s":""}`:""}</div>}
                </div>
              ));
            })()}
            <div className="sc full">
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div><div className="sc-lbl">Total Meals</div><div className="sc-val">{meals.length}</div><div className="sc-ltr">{cheatMeals.length} cheat meal{cheatMeals.length!==1?"s":""}</div></div>
                <div style={{textAlign:"right"}}><div className="sc-lbl">Days Logged</div><div className="sc-val">{Object.keys(dailyAvgsByDate).length}</div><div className="sc-ltr">avg {meals.length&&Object.keys(dailyAvgsByDate).length?(meals.length/Object.keys(dailyAvgsByDate).length).toFixed(1):0} meals/day</div></div>
              </div>
            </div>
          </div>
          <div className="sec-lbl" style={{padding:"4px 16px 10px"}}>Meal Distribution</div>
          <div style={{padding:"0 16px 18px"}}>
            <div className="dist-bar">{dist.map(({b,count})=><div key={b} className="dist-seg" style={{flex:count||0.01,background:BUCKET_COLOR[b].bg,opacity:b==="D"?.55:1}}/>)}</div>
            <div className="dist-legend">{dist.map(({b,count})=><div key={b} className="dist-item"><span className="dist-dot" style={{background:BUCKET_COLOR[b].bg}}/>{b} — {count} ({Math.round((count/total)*100)}%)</div>)}</div>
          </div>
          <div className="sec-lbl" style={{padding:"4px 16px 10px"}}>Cheat Meal History</div>
          {cheatMeals.length===0&&<div className="empty">No cheat meals 🎉</div>}
          {cheatMeals.map(m=>renderMI(m,true))}
          <div style={{height:16}}/>
        </div>}

        {/* ══ LIBRARY ══ */}
        {tab==="library"&&<div className="page-scroll">
          <div className="ph"><div className="ph-title">Meal Library</div><div className="ph-sub">{libraryEntries.length} saved entries · A, B & C only</div></div>
          <div className="lib-toolbar">
            <div className="lib-search-wrap">
              <span className="lib-search-icon">⌕</span>
              <input ref={searchRef} className="lib-search" placeholder="Search meals…" value={libSearch} onChange={e=>setLibSearch(e.target.value)}/>
              {libSearch&&<button className="lib-search-clear" onClick={()=>{setLibSearch("");searchRef.current?.focus();}}>×</button>}
            </div>
            <div className="lib-filters">
              {[["ALL","All"],["A","A — Elite"],["B","B — Good"],["C","C — Average"]].map(([val,lbl])=>(
                <button key={val} className={"lib-filter-btn"+(libBucketFilter===val?" active":"")} onClick={()=>setLibBucketFilter(val)}>{lbl}</button>
              ))}
            </div>
            <div className="lib-sort-row">
              <span className="lib-sort-lbl">Sort</span>
              <select className="lib-sort" value={libSort} onChange={e=>setLibSort(e.target.value)}>
                {SORT_OPTIONS.map(o=><option key={o.val} value={o.val}>{o.label}</option>)}
              </select>
            </div>
          </div>
          {(libSearch||libBucketFilter!=="ALL")&&<div className="lib-results-count">{filteredLib.length} result{filteredLib.length!==1?"s":""}</div>}
          <button className="lib-add-btn" onClick={openLibAdd}><span style={{fontSize:18,lineHeight:1}}>+</span> Add Meal to Library</button>
          {renderLibSection()}
          <div style={{height:16}}/>
        </div>}

        {/* ══ BODY (DASHBOARD) ══ */}
        {tab==="dashboard"&&<div className="page-scroll">
          <div className="ph">
            <div style={{display:"flex",alignItems:"flex-end",justifyContent:"space-between"}}>
              <div className="ph-title">Body</div>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,letterSpacing:".08em",color:"var(--faint)"}}>{fmtDate(today)}</div>
            </div>
            <div className="ph-sub">{dashStreak>1?`${dashStreak}-day logging streak`:"Start your streak today"}</div>
          </div>
          <div className="w-hero">
            <div className="w-hero-label">Current Weight</div>
            <div><span className="w-hero-val">{latestEntry?.weight?.toFixed(1)??"—"}</span><span className="w-hero-unit">lbs</span></div>
            {wDelta!=null&&<div className={"w-hero-delta "+(wDelta>0?"up":wDelta<0?"down":"flat")}>{wDelta>0?"+":""}{wDelta.toFixed(1)} lbs vs yesterday</div>}
            <div className="w-hero-row">
              <div className="w-stat"><div className="w-stat-lbl">7-Day Avg</div><div className="w-stat-val">{(()=>{const s=sortedDash.slice(0,7).map(e=>e.weight).filter(Boolean);return s.length?(s.reduce((a,b)=>a+b,0)/s.length).toFixed(1):"—";})()}</div></div>
              <div className="w-stat"><div className="w-stat-lbl">30-Day Avg</div><div className="w-stat-val">{(()=>{const s=sortedDash.slice(0,30).map(e=>e.weight).filter(Boolean);return s.length?(s.reduce((a,b)=>a+b,0)/s.length).toFixed(1):"—";})()}</div></div>
              <div className="w-stat"><div className="w-stat-lbl">Streak</div><div className="w-stat-val">{dashStreak}d</div></div>
            </div>
            <button className="w-hero-btn" onClick={()=>setWModal({mode:todayDashEntry?.weight?"edit":"add",date:today,entry:todayDashEntry})}>{todayDashEntry?.weight?"Edit":"+ Log"}</button>
          </div>
          <div className="sec-hdr">
            <span className="sec-hdr-lbl">Today's Nutrition</span>
            <button className="sec-hdr-action" onClick={()=>setNModal({mode:todayDashEntry?.protein!=null?"edit":"add",date:today,entry:todayDashEntry})}>{todayDashEntry?.protein!=null?"Edit":"+ Log"}</button>
          </div>
          <div className="nut-row">
            <NutCard label="Protein" val={todayDashEntry?.protein} target={recProtein(latestEntry?.weight)} unit="g" color="var(--steel)" onClick={()=>setNModal({mode:todayDashEntry?.protein!=null?"edit":"add",date:today,entry:todayDashEntry})}/>
            <NutCard label="Calories" val={todayDashEntry?.calories} target={recCalories(latestEntry?.weight)} unit="" color="#B8860B" onClick={()=>setNModal({mode:todayDashEntry?.protein!=null?"edit":"add",date:today,entry:todayDashEntry})}/>
            <NutCard label="Water" val={todayDashEntry?.water} target={8} unit="gl" color="#2E8B57" onClick={()=>setNModal({mode:todayDashEntry?.protein!=null?"edit":"add",date:today,entry:todayDashEntry})}/>
          </div>
          <div className="sec-hdr" style={{paddingBottom:0}}>
            <span className="sec-hdr-lbl" style={{border:"none",padding:0}}>Weight Trend</span>
          </div>
          <div className="chart-wrap">
            <WeightChart entries={dashEntries} chartRange={chartRange} setChartRange={setChartRange} onDotClick={d=>{setDashSelDate(d);}}/>
          </div>
          <div className="sec-hdr">
            <span className="sec-hdr-lbl">Body Calendar</span>
            <div style={{display:"flex",gap:4,alignItems:"center"}}>
              <button className="cal-arrow" style={{fontSize:16,minWidth:36,minHeight:36}} onClick={()=>{if(dashCalMonth===0){setDashCalMonth(11);setDashCalYear(y=>y-1);}else setDashCalMonth(m=>m-1);}}>‹</button>
              <span style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--faint)"}}>{MONTHS[dashCalMonth].slice(0,3)} {dashCalYear}</span>
              <button className="cal-arrow" style={{fontSize:16,minWidth:36,minHeight:36}} onClick={()=>{if(dashCalMonth===11){setDashCalMonth(0);setDashCalYear(y=>y+1);}else setDashCalMonth(m=>m+1);}}>›</button>
            </div>
          </div>
          <div className="cal-grid">
            {DOW.map(d=><div key={d} className="cal-dow">{d}</div>)}
            {dashCalDays.map(({ds,cur})=>{
              const e=dashByDate[ds];
              return(
                <div key={ds} className={"cal-day"+(!cur?" other-month":"")+(ds===today?" today":"")+(ds===dashSelDate?" selected":"")+(e?" has-entry":"")} onClick={()=>setDashSelDate(ds)}>
                  <span className="cal-dn">{parseInt(ds.slice(8))}</span>
                  {e?.weight&&<div className="cal-day-wt">{e.weight.toFixed(1)}</div>}
                  {e?.protein!=null&&<div className="cal-day-nut"/>}
                </div>
              );
            })}
          </div>
          <div className="day-panel" style={{margin:"12px 16px 0"}}>
            <div className="dp-header">
              <div>
                <div className="dp-date">{fmtFull(dashSelDate)}{dashSelDate===today?" · Today":""}</div>
                {dashByDate[dashSelDate]?.weight&&<div className="dp-gpa">{dashByDate[dashSelDate].weight.toFixed(1)} lbs</div>}
              </div>
              <button className="dp-add" onClick={()=>setWModal({mode:dashByDate[dashSelDate]?.weight?"edit":"add",date:dashSelDate,entry:dashByDate[dashSelDate]})}>{dashByDate[dashSelDate]?.weight?"Edit":"+ Log"}</button>
            </div>
            {dashByDate[dashSelDate]?(
              <div className="dp-body">
                {dashByDate[dashSelDate].weight!=null&&<div className="dp-row"><span className="dp-row-lbl">Weight</span><span className="dp-row-val">{dashByDate[dashSelDate].weight.toFixed(1)} lbs</span></div>}
                {dashByDate[dashSelDate].protein!=null&&<div className="dp-row"><span className="dp-row-lbl">Protein</span><span className="dp-row-val" style={{color:"var(--steel)"}}>{dashByDate[dashSelDate].protein}g</span></div>}
                {dashByDate[dashSelDate].calories!=null&&<div className="dp-row"><span className="dp-row-lbl">Calories</span><span className="dp-row-val" style={{color:"#B8860B"}}>{dashByDate[dashSelDate].calories} kcal</span></div>}
                {dashByDate[dashSelDate].water!=null&&<div className="dp-row"><span className="dp-row-lbl">Water</span><span className="dp-row-val" style={{color:"#2E8B57"}}>{dashByDate[dashSelDate].water} gl</span></div>}
                <div style={{marginTop:10}}>
                  <button style={{width:"100%",padding:"9px",background:"var(--steel)",color:"var(--surface)",border:"none",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:10,letterSpacing:".08em"}}
                    onClick={()=>setNModal({mode:dashByDate[dashSelDate]?.protein!=null?"edit":"add",date:dashSelDate,entry:dashByDate[dashSelDate]})}>
                    {dashByDate[dashSelDate]?.protein!=null?"Edit Nutrition":"+ Log Nutrition"}
                  </button>
                </div>
              </div>
            ):<div className="dp-empty">No entry for this day</div>}
          </div>
          <div className="sec-hdr"><span className="sec-hdr-lbl">Recent Entries</span></div>
          {sortedDash.length===0&&<div className="empty">No body entries yet</div>}
          {sortedDash.slice(0,7).map((e,i,arr)=>{
            const prev=arr[i+1];const delta=(e.weight&&prev?.weight)?Math.round((e.weight-prev.weight)*10)/10:null;
            return(
              <div className="hist-item" key={e.id}>
                <div className="hist-date">{fmtDate(e.date)}</div>
                <div className="hist-weight">{e.weight?.toFixed(1)??"—"}</div>
                <div className={"hist-delta"+(delta==null?"":delta>0?" up":" down")}>{delta==null?"":delta>0?`+${delta}`:delta}</div>
                <div className="hist-bars">
                  {e.protein!=null&&<div className="hist-bar-row"><span className="hist-bar-lbl">P</span><div className="hist-bar-track"><div className="hist-bar-fill" style={{width:`${Math.min(100,(e.protein/recProtein(e.weight))*100)}%`,background:"var(--steel)"}}/></div></div>}
                  {e.calories!=null&&<div className="hist-bar-row"><span className="hist-bar-lbl">C</span><div className="hist-bar-track"><div className="hist-bar-fill" style={{width:`${Math.min(100,(e.calories/recCalories(e.weight))*100)}%`,background:"#B8860B"}}/></div></div>}
                  {e.water!=null&&<div className="hist-bar-row"><span className="hist-bar-lbl">W</span><div className="hist-bar-track"><div className="hist-bar-fill" style={{width:`${Math.min(100,(e.water/8)*100)}%`,background:"#2E8B57"}}/></div></div>}
                </div>
                <button className="hist-edit-btn" onClick={()=>setWModal({mode:"edit",date:e.date,entry:e})}>✎</button>
              </div>
            );
          })}
          <div style={{height:16}}/>
        </div>}


        {/* ══ ACTIVITY ══ */}
        {tab==="activity"&&<div className="page-scroll">
          <div className="ph">
            <div style={{display:"flex",alignItems:"flex-end",justifyContent:"space-between"}}>
              <div className="ph-title">Activity</div>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,letterSpacing:".08em",color:"var(--faint)"}}>{fmtDate(todayStr())}</div>
            </div>
            <div className="ph-sub">Exercise time · Steps · Daily grades</div>
          </div>

          {/* Today quick-log hero */}
          <div className="act-hero">
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:".15em",textTransform:"uppercase",color:"var(--steel-lt)",marginBottom:8}}>Today's Activity</div>
            {/* Exercise grade row */}
            <div style={{marginBottom:6}}>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,letterSpacing:".12em",textTransform:"uppercase",color:"rgba(247,245,240,0.45)",marginBottom:6}}>Exercise</div>
              <div className="act-grade-row">
                {ACT_EXERCISE_RANGES.map(({grade,label,min})=>{
                  const active=todayAct?.exercise!=null&&ACT_EXERCISE_GRADE(todayAct.exercise)===grade;
                  return(
                    <div key={grade} className={"act-grade-pill"+(active?" selected":"")}
                      style={{background:ACT_GRADE_COLOR[grade]+(active?"":"33"),cursor:"pointer"}}
                      onClick={()=>setActModal({mode:todayAct?"edit":"add",date:todayStr(),entry:todayAct})}>
                      <div className="act-grade-letter">{grade}</div>
                      <div className="act-grade-range">{label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Steps grade row */}
            <div>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,letterSpacing:".12em",textTransform:"uppercase",color:"rgba(247,245,240,0.45)",marginBottom:6,marginTop:10}}>Steps</div>
              <div className="act-grade-row">
                {ACT_STEPS_RANGES.map(({grade,label,min})=>{
                  const active=todayAct?.steps!=null&&ACT_STEPS_GRADE(todayAct.steps)===grade;
                  return(
                    <div key={grade} className={"act-grade-pill"+(active?" selected":"")}
                      style={{background:ACT_GRADE_COLOR[grade]+(active?"":"33"),cursor:"pointer"}}
                      onClick={()=>setActModal({mode:todayAct?"edit":"add",date:todayStr(),entry:todayAct})}>
                      <div className="act-grade-letter">{grade}</div>
                      <div className="act-grade-range">{label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Current values */}
            {todayAct&&(
              <div className="act-input-row">
                {todayAct.exercise!=null&&<><span className="act-input-lbl">Ex</span><span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:28,color:"var(--surface)"}}>{todayAct.exercise}</span><span className="act-num-unit">min</span></>}
                {todayAct.exercise!=null&&todayAct.steps!=null&&<span style={{color:"rgba(247,245,240,0.2)",margin:"0 4px"}}>·</span>}
                {todayAct.steps!=null&&<><span className="act-input-lbl">Steps</span><span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:28,color:"var(--surface)"}}>{todayAct.steps.toLocaleString()}</span></>}
              </div>
            )}
            <button className="w-hero-btn" onClick={()=>setActModal({mode:todayAct?"edit":"add",date:todayStr(),entry:todayAct})}>{todayAct?"Edit":"+ Log"}</button>
          </div>

          {/* Grade scale reference */}
          <div style={{padding:"14px 16px 0",borderTop:"1px solid var(--border)",marginTop:14}}>
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:".12em",textTransform:"uppercase",color:"var(--faint)",marginBottom:10}}>Grade Scale</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <div style={{background:"var(--surface)",border:"1.5px solid var(--border-strong)",padding:"10px 12px",borderTop:"3px solid var(--steel)"}}>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,letterSpacing:".1em",textTransform:"uppercase",color:"var(--faint)",marginBottom:6}}>Exercise</div>
                {ACT_EXERCISE_RANGES.map(({grade,label})=>(
                  <div key={grade} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"3px 0",borderBottom:"1px solid var(--border)"}}>
                    <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:16,color:ACT_GRADE_COLOR[grade],letterSpacing:".04em"}}>{grade}</span>
                    <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--muted)"}}>{label}</span>
                  </div>
                ))}
              </div>
              <div style={{background:"var(--surface)",border:"1.5px solid var(--border-strong)",padding:"10px 12px",borderTop:"3px solid #2E8B57"}}>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:8,letterSpacing:".1em",textTransform:"uppercase",color:"var(--faint)",marginBottom:6}}>Steps</div>
                {ACT_STEPS_RANGES.map(({grade,label})=>(
                  <div key={grade} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"3px 0",borderBottom:"1px solid var(--border)"}}>
                    <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:16,color:ACT_GRADE_COLOR[grade],letterSpacing:".04em"}}>{grade}</span>
                    <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--muted)"}}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Calendar */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 16px 10px",borderTop:"1px solid var(--border)",marginTop:14}}>
            <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:".14em",textTransform:"uppercase",color:"var(--faint)"}}>Activity Calendar</span>
            <div style={{display:"flex",gap:4,alignItems:"center"}}>
              <button className="cal-arrow" style={{fontSize:16,minWidth:36,minHeight:36}} onClick={()=>{if(actCalMonth===0){setActCalMonth(11);setActCalYear(y=>y-1);}else setActCalMonth(m=>m-1);}}>‹</button>
              <span style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--faint)"}}>{MONTHS[actCalMonth].slice(0,3)} {actCalYear}</span>
              <button className="cal-arrow" style={{fontSize:16,minWidth:36,minHeight:36}} onClick={()=>{if(actCalMonth===11){setActCalMonth(0);setActCalYear(y=>y+1);}else setActCalMonth(m=>m+1);}}>›</button>
            </div>
          </div>
          <div className="cal-grid">
            {DOW.map(d=><div key={d} className="cal-dow">{d}</div>)}
            {actCalDays.map(({ds,cur})=>{
              const e=actByDate[ds];const isSel=ds===actSelDate;const isToday=ds===todayStr();
              return(
                <div key={ds} className={"cal-day"+(!cur?" other-month":"")+(isToday?" today":"")+(isSel?" selected":"")+(e?" has-entry":"")} onClick={()=>setActSelDate(ds)}>
                  <span className="cal-dn">{parseInt(ds.slice(8))}</span>
                  {e?.exercise!=null&&<div className="act-cal-dot-ex" style={{background:isSel?"rgba(247,245,240,.8)":ACT_GRADE_COLOR[ACT_EXERCISE_GRADE(e.exercise)]}}/>}
                  {e?.steps!=null&&<div className="act-cal-dot-st" style={{background:isSel?"rgba(247,245,240,.6)":ACT_GRADE_COLOR[ACT_STEPS_GRADE(e.steps)]}}/>}
                </div>
              );
            })}
          </div>

          {/* Day panel */}
          <div className="act-log-card" style={{margin:"12px 16px 0"}}>
            <div className="act-log-hdr">
              <div>
                <div className="act-log-date">{fmtFull(actSelDate)}{actSelDate===todayStr()?" · Today":""}</div>
                {actByDate[actSelDate]&&<div className="act-log-gpa">
                  {actByDate[actSelDate].exercise!=null&&`Ex: ${ACT_EXERCISE_GRADE(actByDate[actSelDate].exercise)}`}
                  {actByDate[actSelDate].exercise!=null&&actByDate[actSelDate].steps!=null&&" · "}
                  {actByDate[actSelDate].steps!=null&&`Steps: ${ACT_STEPS_GRADE(actByDate[actSelDate].steps)}`}
                </div>}
              </div>
              <button className="act-log-add" onClick={()=>setActModal({mode:actByDate[actSelDate]?"edit":"add",date:actSelDate,entry:actByDate[actSelDate]})}>{actByDate[actSelDate]?"Edit":"+ Log"}</button>
            </div>
            {actByDate[actSelDate]?(
              <div className="act-log-body">
                {actByDate[actSelDate].exercise!=null&&(
                  <div className="act-row">
                    <span className="act-row-lbl">Exercise</span>
                    <div className="act-row-val">
                      <span className="act-row-num">{actByDate[actSelDate].exercise} min</span>
                      <span className="act-grade-badge" style={{background:ACT_GRADE_COLOR[ACT_EXERCISE_GRADE(actByDate[actSelDate].exercise)]}}>{ACT_EXERCISE_GRADE(actByDate[actSelDate].exercise)}</span>
                    </div>
                  </div>
                )}
                {actByDate[actSelDate].steps!=null&&(
                  <div className="act-row">
                    <span className="act-row-lbl">Steps</span>
                    <div className="act-row-val">
                      <span className="act-row-num">{actByDate[actSelDate].steps.toLocaleString()}</span>
                      <span className="act-grade-badge" style={{background:ACT_GRADE_COLOR[ACT_STEPS_GRADE(actByDate[actSelDate].steps)]}}>{ACT_STEPS_GRADE(actByDate[actSelDate].steps)}</span>
                    </div>
                  </div>
                )}
              </div>
            ):<div className="dp-empty">No activity logged for this day</div>}
          </div>

          {/* History */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 16px 10px",borderTop:"1px solid var(--border)",marginTop:14}}>
            <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:".14em",textTransform:"uppercase",color:"var(--faint)"}}>Recent Activity</span>
          </div>
          {sortedAct.length===0&&<div className="empty">No activity logged yet</div>}
          {sortedAct.slice(0,14).map(e=>(
            <div className="act-hist-item" key={e.id}>
              <div className="act-hist-date">{fmtDate(e.date)}</div>
              <div className="act-hist-grades">
                {e.exercise!=null&&<span className="act-hist-pill" style={{background:ACT_GRADE_COLOR[ACT_EXERCISE_GRADE(e.exercise)]}}>Ex {ACT_EXERCISE_GRADE(e.exercise)}</span>}
                {e.steps!=null&&<span className="act-hist-pill" style={{background:ACT_GRADE_COLOR[ACT_STEPS_GRADE(e.steps)]}}>Steps {ACT_STEPS_GRADE(e.steps)}</span>}
              </div>
              <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:2}}>
                {e.exercise!=null&&<span className="act-hist-detail">{e.exercise}m</span>}
                {e.steps!=null&&<span className="act-hist-detail">{e.steps.toLocaleString()}</span>}
              </div>
              <button className="hist-edit-btn" onClick={()=>setActModal({mode:"edit",date:e.date,entry:e})}>✎</button>
            </div>
          ))}
          <div style={{height:16}}/>
        </div>}

        {renderLogModal()}
        {renderLibModal()}
        {wModal&&<WeightModal initial={wModal.entry?.weight??null} date={wModal.date} seedWeight={prevWeight} isEdit={wModal.mode==="edit"&&wModal.entry?.weight!=null} onSave={addOrUpdateWeight} onDelete={()=>deleteWeightEntry(wModal.date)} onClose={()=>setWModal(null)}/>}
        {nModal&&<NutModal initial={nModal.entry} date={nModal.date} seedWeight={prevWeight??180} isEdit={nModal.mode==="edit"&&nModal.entry?.protein!=null} onSave={addOrUpdateNutrition} onDelete={()=>deleteNutritionEntry(nModal.date)} onClose={()=>setNModal(null)}/>}

        {actModal&&<ActivityModal initial={actModal.entry} date={actModal.date} isEdit={actModal.mode==="edit"&&!!actModal.entry} onSave={addOrUpdateActivity} onDelete={()=>deleteActivity(actModal.date)} onClose={()=>setActModal(null)}/>}

        {/* ── APPS DRAWER ── */}
        {appsOpen&&(
          <div className="apps-overlay" onClick={e=>e.target===e.currentTarget&&setAppsOpen(false)}>
            <div className="apps-drawer">
              <div className="apps-drag"/>
              <div className="apps-title">Fitnessgrade Suite<span>fitnessgrade.com</span></div>
              <div className="apps-grid">
                {[
                  {icon:"🏋️",name:"Iron + Cable",sub:"Barbell · Machine",url:"https://gym.fitnessgrade.com",current:false},
                  {icon:"🪨",name:"Kettle + Iron",sub:"Dumbbell · Kettlebell",url:"https://dumbell.fitnessgrade.com",current:false},
                  {icon:"🔥",name:"Insanity",sub:"Bodyweight · Timer",url:"https://insanity.fitnessgrade.com",current:false},
                  {icon:"✅",name:"Health Gates",sub:"Wellness · Tracker",url:"https://check.fitnessgrade.com",current:false},
                  {icon:"⛺",name:"Camp Rental",sub:"Gear · Booking",url:"https://camprental.fitnessgrade.com",current:false},
                  {icon:"📊",name:"Fitnessgrade",sub:"Meals · GPA",url:"https://fitnessgrade.com",current:true},
                ].map(app=>(
                  <a key={app.url} href={app.url}
                    className={"app-tile"+(app.current?" current":"")}
                    target={app.current?"_self":"_blank"}
                    rel="noopener noreferrer"
                    onClick={()=>setAppsOpen(false)}>
                    <div className="app-tile-icon">{app.icon}</div>
                    <div className="app-tile-name">{app.name}</div>
                    <div className="app-tile-sub">{app.sub}</div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}
        <div className={"toast"+(toastVis?" show":"")}>{toastMsg}</div>
        <nav className="bottom-nav">
          {[["home","⊙","Home"],["calendar","▦","Cal"],["analytics","≈","Stats"],["library","☰","Library"],["dashboard","◎","Body"],["activity","◈","Activity"]].map(([k,icon,l])=>(
            <button key={k} className={"nav-btn"+(tab===k?" active":"")} onClick={()=>setTab(k)}>
              <span className="nav-icon">{icon}</span>{l}
            </button>
          ))}
          <button className="nav-btn" onClick={()=>setAppsOpen(true)} title="All Apps">
            <span className="nav-icon">⊞</span>Apps
          </button>
        </nav>
      </div>
    </>
  );
}
